// =============================================================================
// VRP-HOOK-001: FILE STRUCTURE OVERVIEW
// =============================================================================
// This file provides comprehensive React hooks for the VRP System v4
// Architecture documentation: frontend/src/hooks/useVRPData-architecture.md (v1.1)
// Total hooks: 70+ entity data hooks, 15+ utility hooks, 1 mega-hook (useTableData)
// Real-time sync: Convex WebSocket integration for reactive state
// Integration: Powers TableEditor v3.0 (TBL-ARCH-038 to 040, TBL-PERF-003 to 006)

// VRP-HOOK-002: IMPORT DEPENDENCIES
// Core Convex hooks for real-time database operations
import {
  useQuery,
  useMutation,
  useAction,
  usePaginatedQuery,
} from 'convex/react'
import { api } from '../../../convex/_generated/api'
import type { Id } from '../../../convex/_generated/dataModel'
import { useEffect, useState, useRef, useCallback, useMemo } from 'react'
import { toast } from 'sonner'
import { debounce, secondsToTimeString } from '@/lib/utils'
import { perfDebug } from '@/lib/performance-tracker'
import { useDeepCompareMemo } from './useDeepCompare'
import type {
  FilterState,
  FilterFieldState,
  FilterConfig,
  FilterValue,
  FilterOperator,
  FilterPerformanceMetrics,
  VRPTableType,
} from '@/components/table-editor/bulk-operations/types/shared.types'

// =============================================================================
// VRP-HOOK-011 TO 020: ENHANCED DATE/TIME CONVERSION UTILITIES WITH TIMEZONE SUPPORT
// =============================================================================

// VRP-HOOK-011: Timezone Validation Utility
// Validates IANA timezone strings using Intl.DateTimeFormat API
/**
 * Validate timezone string using Intl API
 */
export const isValidTimezone = (timezone: string): boolean => {
  try {
    new Intl.DateTimeFormat('en', { timeZone: timezone })
    return true
  } catch {
    return false
  }
}

// VRP-HOOK-012: UTC to Local DateTime Conversion
// Converts UTC milliseconds to local date/time using Intl.DateTimeFormat for proper timezone handling
/**
 * Convert UTC milliseconds to local date/time with timezone support
 */
export const convertUTCToLocalDateTime = (
  utcMs: number,
  timezone: string = 'UTC'
) => {
  try {
    const date = new Date(utcMs)

    // Use Intl.DateTimeFormat for proper timezone conversion
    const dateFormatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })

    const timeFormatter = new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
    })

    return {
      date: dateFormatter.format(date), // YYYY-MM-DD format
      time: timeFormatter.format(date), // HH:MM format
      formatted: date.toLocaleString('en-US', {
        timeZone: timezone,
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
      }),
    }
  } catch (error) {
    console.error('Date conversion error:', error)
    return { date: '', time: '', formatted: 'Invalid date' }
  }
}

// VRP-HOOK-013: CRITICAL FIX - Local DateTime to UTC Conversion (Binary Search Algorithm)
// Uses binary search to find UTC timestamp that produces target local time in specified timezone
// FIXED: Reliable Intl.DateTimeFormat approach treating user input as project timezone
// See architecture docs for detailed binary search algorithm explanation
/**
 * Convert local date/time to UTC milliseconds with timezone support
 * FIXED: Uses reliable Intl.DateTimeFormat approach treating user input as project timezone
 */
export const convertLocalDateTimeToUTC = (
  date: string,
  time: string,
  timezone: string = 'UTC'
): number => {
  try {
    if (!date || !time) return 0

    // Validate date format (YYYY-MM-DD)
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/
    if (!dateRegex.test(date)) {
      throw new Error(`Invalid date format: ${date}. Expected YYYY-MM-DD`)
    }

    // Validate time format (HH:MM)
    const timeRegex = /^\d{1,2}:\d{2}$/
    if (!timeRegex.test(time)) {
      throw new Error(`Invalid time format: ${time}. Expected HH:MM`)
    }

    // Validate timezone
    if (!isValidTimezone(timezone)) {
      throw new Error(`Invalid timezone: ${timezone}`)
    }

    // Pad time to ensure HH:MM format
    const paddedTime = time.padStart(5, '0')

    // For UTC, use direct parsing
    if (timezone === 'UTC') {
      const localDateTimeString = `${date}T${paddedTime}:00`
      return new Date(localDateTimeString + 'Z').getTime()
    }

    // CRITICAL FIX: Use Intl.DateTimeFormat to handle timezone conversion properly
    // Split date and time components
    const [year, month, day] = date.split('-').map(Number)
    const [hour, minute] = paddedTime.split(':').map(Number)

    // Create a date in the target timezone by finding the UTC time that produces the desired local time
    // We'll test different UTC times until we find one that formats to our target local time

    // Start with an approximation: treat the input as if it were UTC
    const testDate = new Date(
      Date.UTC(year, month - 1, day, hour, minute, 0, 0)
    )

    // Use Intl.DateTimeFormat to check what time this UTC timestamp shows in target timezone
    const formatter = new Intl.DateTimeFormat('sv-SE', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })

    const targetDateTimeString = `${date}T${paddedTime}:00`

    // Binary search approach to find the correct UTC time
    let low = testDate.getTime() - 86400000 // -24 hours
    let high = testDate.getTime() + 86400000 // +24 hours
    let bestMatch = testDate.getTime()

    for (let i = 0; i < 20; i++) {
      // Max 20 iterations for binary search
      const mid = Math.round((low + high) / 2)
      const midDate = new Date(mid)

      const formattedInTargetTz = formatter.format(midDate).replace(' ', 'T')

      if (formattedInTargetTz === targetDateTimeString) {
        return mid // Exact match found
      }

      bestMatch = mid // Keep track of best match

      if (formattedInTargetTz < targetDateTimeString) {
        low = mid + 1
      } else {
        high = mid - 1
      }

      // If we're within 1 second, that's close enough for minute precision
      if (high - low < 1000) break
    }

    // Final validation check
    const finalFormatted = formatter
      .format(new Date(bestMatch))
      .replace(' ', 'T')
    if (finalFormatted === targetDateTimeString) {
      return bestMatch
    }

    // If binary search didn't work perfectly, try a direct calculation approach
    // This handles edge cases like DST transitions more reliably
    const offsetAtTargetTime = getTimezoneOffset(
      year,
      month - 1,
      day,
      hour,
      minute,
      timezone
    )
    const directResult =
      new Date(year, month - 1, day, hour, minute, 0, 0).getTime() +
      offsetAtTargetTime

    // Validate the direct result
    const directFormatted = formatter
      .format(new Date(directResult))
      .replace(' ', 'T')
    if (directFormatted === targetDateTimeString) {
      return directResult
    }

    console.warn(
      `Timezone conversion using best approximation for ${date} ${time} in ${timezone}`
    )
    return bestMatch
  } catch (error) {
    console.error('DateTime to UTC conversion error:', {
      date,
      time,
      timezone,
      error,
    })
    throw new Error(
      `Failed to convert ${date} ${time} in ${timezone} to UTC: ${error instanceof Error ? error.message : 'Unknown error'}`
    )
  }
}

// VRP-HOOK-014: Timezone Offset Helper Function
// Calculates timezone offset for specific date/time (supports DST transitions)
/**
 * Helper function to get timezone offset for a specific date/time
 */
function getTimezoneOffset(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  timezone: string
): number {
  try {
    // Create two dates: one in UTC and one in local browser time
    const utcDate = new Date(Date.UTC(year, month, day, hour, minute, 0, 0))
    const localDate = new Date(year, month, day, hour, minute, 0, 0)

    // Get the timezone offset for the target timezone at this date
    const targetFormatter = new Intl.DateTimeFormat('en', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'longOffset',
    })

    // Use the browser's local timezone as reference
    const browserOffset = localDate.getTimezoneOffset() * 60 * 1000

    // This is a simplified approach - for complex timezone handling,
    // the binary search above is more reliable
    return browserOffset
  } catch (error) {
    console.warn('Failed to calculate timezone offset, using UTC:', error)
    return 0
  }
}

// VRP-HOOK-015: Duration Extraction from Milliseconds
// Extracts hours and minutes from milliseconds for relative time window calculations
/**
 * Extract duration components from milliseconds for relative time windows
 */
export const extractDurationFromMs = (ms: number) => {
  const totalMinutes = Math.floor(ms / (1000 * 60))
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return { hours, minutes, totalMinutes }
}

// VRP-HOOK-016: Time Window Validation
// Validates time window ranges and enforces maximum duration limits
/**
 * Validate time window ranges and dates
 */
export const validateTimeWindow = (
  start: number,
  end: number,
  maxDurationHours: number = 24
): string | null => {
  if (start >= end) {
    return 'Start time must be before end time'
  }

  const durationHours = (end - start) / (1000 * 60 * 60)
  if (durationHours > maxDurationHours) {
    return `Time window cannot exceed ${maxDurationHours} hours`
  }

  return null // Valid
}

// VRP-HOOK-017: Absolute Date Validation
// Validates that absolute dates are not in the past for planning purposes
/**
 * Validate absolute date is not in the past
 */
export const validateAbsoluteDate = (utcMs: number): string | null => {
  const now = Date.now()
  if (utcMs < now) {
    const hoursAgo = Math.floor((now - utcMs) / (1000 * 60 * 60))
    return `Date is ${hoursAgo} hours in the past`
  }
  return null // Valid
}

// VRP-HOOK-018: Planning Horizon Formatting
// Formats planning horizon UTC timestamps for display with timezone support
/**
 * Format planning horizon for display
 */
export const formatPlanningHorizon = (
  startTimeUTC: number,
  timezone: string
) => {
  const converted = convertUTCToLocalDateTime(startTimeUTC, timezone)
  return {
    shortFormat: converted.formatted,
    dateOnly: converted.date,
    timeOnly: converted.time,
    fullContext: `Planning starts: ${converted.formatted}`,
  }
}

// =============================================================================
// VRP-HOOK-019: QUERY PERFORMANCE TRACKING UTILITIES
// =============================================================================

/**
 * Track when a Convex query resolves with performance metrics
 * Phase 2.3: Query-Level Performance Tracking
 *
 * @param queryName - Name of the query (e.g., 'vehicles', 'skills')
 * @param data - The resolved data
 * @param isLoading - Whether query is still loading
 */
function trackQueryResolution(
  queryName: string,
  data: unknown,
  isLoading: boolean
): void {
  if (
    import.meta.env.DEV &&
    !isLoading &&
    data !== undefined &&
    data !== null
  ) {
    // Calculate record count based on data type
    const recordCount = Array.isArray(data)
      ? data.length
      : data && typeof data === 'object'
        ? 1
        : 0

    // Detect if cached (Convex sets _cached flag on query results)
    const isCached = (data as any)?._cached === true

    // Mark the query completion with metadata
    markStepOnce(`data:${queryName}`, {
      recordCount,
      cached: isCached,
      queryName: `api.${queryName}`,
      dataType: Array.isArray(data) ? 'array' : typeof data,
    })
  }
}

// =============================================================================
// VRP-HOOK-021 TO 025: ENHANCED FILTER TYPE SYSTEM - FIX CRITICAL TYPE SAFETY VIOLATIONS
// =============================================================================

// VRP-HOOK-021: Base Table Record Interface
// Foundation interface for all VRP table types with common fields
/**
 * Properly typed table record interfaces to replace `any` usage
 */
interface BaseTableRecord {
  readonly _id: string
  readonly _creationTime: number
  readonly updatedAt: number
  readonly projectId: string
  readonly scenarioId?: string
  readonly datasetId?: string
}

// VRP-HOOK-022: Vehicle Record Interface
// Type-safe vehicle record with capacity, skills, time windows
interface VehicleRecord extends BaseTableRecord {
  readonly description?: string
  readonly reference?: string
  readonly profile?: string
  readonly capacity?: ReadonlyArray<number>
  readonly skills?: ReadonlyArray<number>
  readonly startLon?: number
  readonly startLat?: number
  readonly endLon?: number
  readonly endLat?: number
  readonly startLocationId?: string
  readonly endLocationId?: string
  readonly timeWindow?: ReadonlyArray<number>
  readonly costFixed?: number
  readonly costPerHour?: number
  readonly costPerKm?: number
}

// VRP-HOOK-023: JobRecord Interface
interface JobRecord extends BaseTableRecord {
  readonly locationId?: string
  readonly description?: string
  readonly reference?: string
  readonly locationLon?: number
  readonly locationLat?: number
  readonly setup?: number
  readonly service?: number
  readonly delivery?: ReadonlyArray<number>
  readonly pickup?: ReadonlyArray<number>
  readonly skills?: ReadonlyArray<number>
  readonly priority?: number
  readonly timeWindow?: {
    windows: ReadonlyArray<ReadonlyArray<number>>
    format: 'relative' | 'absolute'
    timezone?: string
    planningHorizonStart?: number
  }
  readonly deliveryItems?: ReadonlyArray<{
    productId: string
    quantity: number
    unit: string
  }>
  readonly pickupItems?: ReadonlyArray<{
    productId: string
    quantity: number
    unit: string
  }>
}

// VRP-HOOK-024: LocationRecord, RouteRecord, SkillRecord, ProductRecord
interface LocationRecord extends BaseTableRecord {
  readonly name: string
  readonly address?: string
  readonly description?: string
  readonly locationLon?: number
  readonly locationLat?: number
  readonly locationType?: string
  readonly operatingHours?: string
  readonly contactInfo?: string
  readonly timezone?: string
  readonly clusterId?: string
  readonly geocodeQuality?: string
  readonly geocodeSource?: string
  readonly usageCount?: number
}

interface RouteRecord extends BaseTableRecord {
  readonly optimizationRunId?: string
  readonly vehicleId?: string
  readonly cost?: number
  readonly distance?: number
  readonly duration?: number
  readonly waitingTime?: number
  readonly serviceTime?: number
  readonly setupTime?: number
  readonly deliveries?: ReadonlyArray<number>
  readonly pickups?: ReadonlyArray<number>
  readonly priority?: number
  readonly deliveryCount?: number
  readonly violations?: ReadonlyArray<{
    type: string
    cause: string
    severity?: string
  }>
  readonly geometry?: string
  readonly geojson?: unknown
  readonly vehicleInfo?: {
    description?: string
    reference?: string
    profile?: string
  }
}

interface SkillRecord extends BaseTableRecord {
  readonly name?: string
  readonly description?: string
  readonly skillCode?: string
  readonly category?: string
  readonly subcategory?: string
  readonly parentSkillId?: string
  readonly skillType?: string
  readonly requiresCertification?: boolean
  readonly certificationAuthority?: string
  readonly optimizerSkillId?: number
  readonly isActive?: boolean
}

interface ProductRecord extends BaseTableRecord {
  readonly name: string
  readonly unitType: string
  readonly category?: string
  readonly weightPerUnitKg?: number
  readonly volumePerUnitM3?: number
  readonly lengthCm?: number
  readonly widthCm?: number
  readonly heightCm?: number
  readonly stackable?: boolean
  readonly requiresRefrigeration?: boolean
  readonly hazmatClass?: string
  readonly handlingInstructions?: string
  readonly compartmentType?: string
  readonly densityKgPerUnit?: number
  readonly volumePerUnit?: number
}

// VRP-HOOK-025: TableRecord Union Type
/**
 * Union type for all table record types
 */
type TableRecord =
  | VehicleRecord
  | JobRecord
  | LocationRecord
  | RouteRecord
  | SkillRecord
  | ProductRecord

/**
 * Enhanced filter performance metrics with specific thresholds
 */
interface EnhancedFilterPerformanceMetrics extends FilterPerformanceMetrics {
  readonly memoryUsage: number
  readonly lastFilterTime: number
  readonly slowFilterWarnings: ReadonlyArray<string>
  readonly optimizationRecommendations: ReadonlyArray<string>
  readonly isWithinSLO: boolean // Service Level Objective: sub-50ms
}

/**
 * Enhanced filter state with comprehensive error handling
 */
interface EnhancedFilterState extends FilterState {
  readonly error: string | null
  readonly isLoading: boolean
  readonly performanceMetrics: EnhancedFilterPerformanceMetrics
  readonly appliedFilterCount: number
  readonly resultCount: number
  readonly originalDataCount: number
}

/**
 * Filter options for the universal hook
 */
interface UseTableFilterOptions {
  readonly debounceMs?: number
  readonly enablePerformanceMonitoring?: boolean
  readonly maxRecordsBeforeServerFiltering?: number
  readonly enableVirtualScrolling?: boolean
  readonly cacheKey?: string
  readonly persistState?: boolean
  readonly performanceThresholdMs?: number
}

/**
 * Return type for the filter hook with proper typing
 */
interface UseTableFilterReturn<T extends TableRecord> {
  // Filtered data with proper typing
  readonly filteredData: ReadonlyArray<T>
  readonly filterState: EnhancedFilterState
  readonly filterCriteria: Record<string, FilterFieldState> // CRITICAL: Actual filter values (source of truth)
  readonly isFiltering: boolean
  readonly hasActiveFilters: boolean

  // Filter actions with proper typing
  readonly updateGlobalSearch: (searchTerm: string) => void
  readonly updateFieldFilter: (
    fieldKey: string,
    operator: FilterOperator,
    value: FilterValue
  ) => void
  readonly clearFilter: (fieldKey: string) => void
  readonly clearAllFilters: () => void
  readonly toggleFilterVisibility: (fieldKey: string) => void

  // Utility functions
  readonly getFilterSummary: () => string
  readonly exportFilteredData: () => ReadonlyArray<T>
  readonly resetPerformanceMetrics: () => void
  readonly getPerformanceReport: () => EnhancedFilterPerformanceMetrics
}

// =============================================================================
// VRP-HOOK-026 TO 030: TIME FORMATTING UTILITIES FOR VRP SYSTEM
// =============================================================================

/**
// VRP-HOOK-026: useTimeFormat Hook Overview
 * Custom hook for VRP time formatting and conversion
 * Provides utilities for converting between user-friendly time formats and backend seconds
 */
export const useTimeFormat = () => {
  // VRP-HOOK-027: formatDuration (NO FALLBACKS)
  // Format seconds to user-friendly duration
  const formatDuration = useCallback((seconds: number | undefined) => {
    if (seconds === undefined || seconds === null || isNaN(seconds))
      return 'Not set'
    if (seconds === 0) return '0min'

    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)

    if (hours > 0 && minutes > 0) {
      return `${hours}h ${minutes}m`
    } else if (hours > 0) {
      return `${hours} hour${hours === 1 ? '' : 's'}`
    } else {
      return `${minutes} minute${minutes === 1 ? '' : 's'}`
    }
  }, [])

  // VRP-HOOK-028: formatTimeWindow
  // Format seconds to time string using centralized utility
  const formatTimeWindow = useCallback(
    (timeWindow: number[] | undefined, format: '12h' | '24h' = '12h') => {
      if (!timeWindow || timeWindow.length !== 2) return 'Not set'

      return `${secondsToTimeString(timeWindow[0], format)} - ${secondsToTimeString(timeWindow[1], format)}`
    },
    []
  )

  // VRP-HOOK-029: formatTimeWindows
  // Format multiple time windows (timeWindow object format)
  const formatTimeWindows = useCallback(
    (
      timeWindow:
        | {
            windows: number[][]
            format: 'relative' | 'absolute'
            timezone?: string
          }
        | undefined,
      format: '12h' | '24h' = '12h'
    ) => {
      if (!timeWindow || !timeWindow.windows || timeWindow.windows.length === 0)
        return 'Not set'

      if (timeWindow.windows.length === 1) {
        return formatTimeWindow(timeWindow.windows[0], format)
      }

      return timeWindow.windows
        .map(
          (window, index) => `${index + 1}: ${formatTimeWindow(window, format)}`
        )
        .join(', ')
    },
    [formatTimeWindow]
  )

  // VRP-HOOK-030: formatTimeWindowDisplay with Timezone
  // Format TimeWindow object for display with enhanced timezone support
  // FIXED: Accept project timezone context for proper fallback
  const formatTimeWindowDisplay = useCallback(
    (
      timeWindow: any,
      format: '12h' | '24h' = '12h',
      projectTimezone?: string
    ) => {
      if (!timeWindow || typeof timeWindow !== 'object') return 'Not set'

      const {
        windows,
        format: twFormat,
        timezone,
        planningHorizonStart,
      } = timeWindow

      if (!Array.isArray(windows) || windows.length === 0) {
        return 'No windows'
      }

      // CRITICAL FIX: Use project timezone as fallback if timeWindow doesn't specify timezone
      const effectiveTimezone = timezone || projectTimezone || 'UTC'

      if (twFormat === 'absolute') {
        // Format absolute time windows with enhanced multi-day support
        const formattedWindows = windows
          .map((window: number[]) => {
            const start = convertUTCToLocalDateTime(
              window[0],
              effectiveTimezone
            )
            const end = convertUTCToLocalDateTime(window[1], effectiveTimezone)

            // Check if start and end are on different days
            const isMultiDay = start.date !== end.date

            if (isMultiDay) {
              // Multi-day: show both dates with times
              return `${start.date} ${start.time} - ${end.date} ${end.time}`
            } else {
              // Same day: show date once
              return `${start.date} ${start.time} - ${end.time}`
            }
          })
          .join(', ')

        // Simplified timezone display - remove redundant "Absolute" text
        const timezoneDisplay =
          effectiveTimezone !== 'UTC' ? ` ${effectiveTimezone}` : ''
        return `${formattedWindows}${timezoneDisplay}`
      } else {
        // Format relative time windows with duration
        const formattedWindows = windows
          .map((window: number[]) => {
            const startDuration = extractDurationFromMs(window[0])
            const endDuration = extractDurationFromMs(window[1])
            return `${startDuration.hours}h${startDuration.minutes}m - ${endDuration.hours}h${endDuration.minutes}m`
          })
          .join(', ')

        return `${formattedWindows} [Relative]`
      }
    },
    []
  )

  // Check if a field is a time-related field
  const isTimeField = useCallback((fieldKey: string, fieldType?: string) => {
    const timeFields = ['service', 'setup', 'maxTravelTime', 'maxDistance']
    const timeWindowFields = [
      'timeWindow',
      'pickupTimeWindow',
      'deliveryTimeWindow',
    ]

    return (
      timeFields.includes(fieldKey) ||
      timeWindowFields.includes(fieldKey) ||
      fieldType === 'time_range' ||
      fieldType === 'timestamp' ||
      fieldType === 'timewindow_v2' ||
      // Handle time window fields that have dataType 'array' in backend schema
      (fieldType === 'array' && timeWindowFields.includes(fieldKey))
    )
  }, [])

  // Get appropriate time input component type
  const getTimeInputType = useCallback(
    (fieldKey: string, fieldType?: string) => {
      const durationFields = [
        'service',
        'setup',
        'maxTravelTime',
        'maxDistance',
      ]
      const timeWindowFields = [
        'timeWindow',
        'pickupTimeWindow',
        'deliveryTimeWindow',
      ]

      if (durationFields.includes(fieldKey)) {
        return 'duration'
      } else if (timeWindowFields.includes(fieldKey)) {
        return 'timeWindow'
      } else if (fieldType === 'timestamp') {
        return 'timestamp'
      } else if (fieldType === 'timewindow_v2') {
        return 'timeWindow'
      }

      return null
    },
    []
  )

  // Format any time value for display
  // FIXED: Accept project timezone context for timeWindow formatting
  const formatTimeValue = useCallback(
    (
      value: any,
      fieldKey: string,
      fieldType?: string,
      projectTimezone?: string
    ) => {
      const inputType = getTimeInputType(fieldKey, fieldType)

      switch (inputType) {
        case 'duration':
          return formatDuration(value)
        case 'timestamp':
          if (typeof value === 'number') {
            return new Date(value).toLocaleString()
          }
          return 'Not set'
        case 'timeWindow':
          return formatTimeWindowDisplay(value, '12h', projectTimezone)
        default:
          return value?.toString() || 'Not set'
      }
    },
    [
      formatDuration,
      formatTimeWindow,
      formatTimeWindows,
      formatTimeWindowDisplay,
      getTimeInputType,
    ]
  )

  return {
    formatDuration,
    formatTimeWindow,
    formatTimeWindows,
    formatTimeWindowDisplay,
    formatTimeValue,
    isTimeField,
    getTimeInputType,
  }
}

// NOTE: Products CRUD operations are now available via React hooks below:
// - useProducts() - List products by project
// - useProduct() - Get single product
// - useCreateProduct() - Create new product
// - useUpdateProduct() - Update existing product
// - useDeleteProduct() - Delete product
// Products are supported in bulk operations (import/export/templates) via VRPTableType='products'

// =============================================================================
// VRP-HOOK-031 TO 035: PERFORMANCE OPTIMIZATIONS
// =============================================================================

// VRP-HOOK-032: useOptimizedQuery with Caching
// Optimized query with caching
const useOptimizedQuery = <T>(
  query: any,
  args: any,
  cacheKey?: string,
  cacheDuration = 300000 // 5 minutes
) => {
  const cacheRef = useRef<{ [key: string]: { data: T; timestamp: number } }>({})
  const data = useQuery(query, args)

  return useMemo(() => {
    if (!cacheKey || !data) return data

    const now = Date.now()
    const cached = cacheRef.current[cacheKey]

    if (cached && now - cached.timestamp < cacheDuration) {
      return cached.data
    }

    cacheRef.current[cacheKey] = { data, timestamp: now }
    return data
  }, [data, cacheKey, cacheDuration])
}

// VRP-HOOK-033: useDebouncedQuery
// Debounced query hook to prevent excessive API calls
const useDebouncedQuery = <T>(query: any, args: any, delay = 300) => {
  const [debouncedArgs, setDebouncedArgs] = useState(args)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedArgs(args)
    }, delay)

    return () => clearTimeout(timer)
  }, [args, delay])

  return useQuery(query, debouncedArgs)
}

// VRP-HOOK-034: useDataSelector Memoization
// Memoized selector hook for large datasets
const useDataSelector = <T, R>(
  data: T[] | undefined,
  selector: (item: T) => R,
  deps: any[] = []
) => {
  return useMemo(() => {
    if (!data) return undefined
    return data.map(selector)
  }, [data, ...deps])
}

// =============================================================================
// VRP-HOOK-036 TO 040: PROJECT HOOKS
// =============================================================================
// VRP-HOOK-037: useProjects with UAT Integration
// VRP-HOOK-038: useProject, useProjectStats
// VRP-HOOK-039: Project CRUD Operations (create, update, delete)
// VRP-HOOK-040: useSearchProjects

export const useProjects = () => {
  const projects = useQuery(api.projects.list)

  // Update UAT health check with projects load state
  useEffect(() => {
    if (
      process.env.NODE_ENV === 'development' &&
      (window as any).__UAT_HEALTH__
    ) {
      if (projects === undefined) {
        ;(window as any).__UAT_HEALTH__.setLoadState('projects', 'loading')
      } else if (projects === null) {
        ;(window as any).__UAT_HEALTH__.setLoadState('projects', 'error')
      } else {
        ;(window as any).__UAT_HEALTH__.setLoadState('projects', 'success')
      }
    }
  }, [projects])

  return projects
}

export const useProject = (projectId: Id<'projects'> | undefined) => {
  return useQuery(api.projects.getById, projectId ? { id: projectId } : 'skip')
}

export const useProjectStats = (projectId: Id<'projects'> | undefined) => {
  return useQuery(api.projects.getStats, projectId ? { id: projectId } : 'skip')
}

export const useCreateProject = () => {
  return useMutation(api.projects.create)
}

export const useUpdateProject = () => {
  return useMutation(api.projects.update)
}

export const useDeleteProject = () => {
  return useMutation(api.projects.remove)
}

export const useSearchProjects = (query: string) => {
  return useQuery(api.projects.search, query ? { query } : 'skip')
}

// =============================================================================
// VRP-HOOK-041 TO 045: SCENARIO HOOKS
// =============================================================================
// VRP-HOOK-042: useScenarios with UAT Integration
// VRP-HOOK-043: useScenario, useScenarioStats
// VRP-HOOK-044: Scenario CRUD Operations (create, update, delete, clone)

export const useScenarios = (projectId: Id<'projects'> | undefined) => {
  const scenarios = useQuery(
    api.scenarios.listByProject,
    projectId ? { projectId } : 'skip'
  )

  // Update UAT health check with scenarios load state
  useEffect(() => {
    if (
      process.env.NODE_ENV === 'development' &&
      (window as any).__UAT_HEALTH__
    ) {
      if (scenarios === undefined) {
        ;(window as any).__UAT_HEALTH__.setLoadState('scenarios', 'loading')
      } else if (scenarios === null) {
        ;(window as any).__UAT_HEALTH__.setLoadState('scenarios', 'error')
      } else {
        ;(window as any).__UAT_HEALTH__.setLoadState('scenarios', 'success')
      }
    }
  }, [scenarios])

  return scenarios
}

export const useScenario = (scenarioId: Id<'scenarios'> | undefined) => {
  return useQuery(
    api.scenarios.getById,
    scenarioId ? { id: scenarioId } : 'skip'
  )
}

export const useScenarioStats = (scenarioId: Id<'scenarios'> | undefined) => {
  return useQuery(
    api.scenarios.getStats,
    scenarioId ? { id: scenarioId } : 'skip'
  )
}

export const useCreateScenario = () => {
  return useMutation(api.scenarios.create)
}

export const useUpdateScenario = () => {
  return useMutation(api.scenarios.update)
}

export const useDeleteScenario = () => {
  return useMutation(api.scenarios.remove)
}

export const useCloneScenario = () => {
  return useMutation(api.scenarios.clone)
}

// =============================================================================
// VRP-HOOK-046 TO 050: DATASET HOOKS
// =============================================================================
// VRP-HOOK-047: useDatasets with UAT Integration
// VRP-HOOK-048: useDataset, useDatasetStats
// VRP-HOOK-049: Dataset CRUD Operations (create, update, delete, clone, archive)

export const useDatasets = (scenarioId: Id<'scenarios'> | undefined) => {
  const datasets = useQuery(
    api.datasets.listByScenario,
    scenarioId ? { scenarioId } : 'skip'
  )

  // Update UAT health check with datasets load state
  useEffect(() => {
    if (
      process.env.NODE_ENV === 'development' &&
      (window as any).__UAT_HEALTH__
    ) {
      if (datasets === undefined) {
        ;(window as any).__UAT_HEALTH__.setLoadState('datasets', 'loading')
      } else if (datasets === null) {
        ;(window as any).__UAT_HEALTH__.setLoadState('datasets', 'error')
      } else {
        ;(window as any).__UAT_HEALTH__.setLoadState('datasets', 'success')
      }
    }
  }, [datasets])

  return datasets
}

export const useDatasetsByProject = (projectId: Id<'projects'> | undefined) => {
  return useQuery(
    api.datasets.listByProject,
    projectId ? { projectId } : 'skip'
  )
}

export const useDataset = (datasetId: Id<'datasets'> | undefined) => {
  return useQuery(api.datasets.getById, datasetId ? { id: datasetId } : 'skip')
}

export const useDatasetStats = (datasetId: Id<'datasets'> | undefined) => {
  return useQuery(api.datasets.getStats, datasetId ? { id: datasetId } : 'skip')
}

export const useCreateDataset = () => {
  return useMutation(api.datasets.create)
}

export const useUpdateDataset = () => {
  return useMutation(api.datasets.update)
}

export const useDeleteDataset = () => {
  return useMutation(api.datasets.remove)
}

export const useCloneDataset = () => {
  return useMutation(api.datasets.clone)
}

export const useArchiveDataset = () => {
  return useMutation(api.datasets.archive)
}

// =============================================================================
// VRP-HOOK-051 TO 055: VEHICLE HOOKS
// =============================================================================

export const useVehicles = (datasetId: Id<'datasets'> | undefined) => {
  const vehicles = useQuery(
    api.vehicles.listByDataset,
    datasetId ? { datasetId } : 'skip'
  )

  // Update UAT health check with vehicles load state
  useEffect(() => {
    if (
      process.env.NODE_ENV === 'development' &&
      (window as any).__UAT_HEALTH__
    ) {
      if (vehicles === undefined) {
        ;(window as any).__UAT_HEALTH__.setLoadState('vehicles', 'loading')
      } else if (vehicles === null) {
        ;(window as any).__UAT_HEALTH__.setLoadState('vehicles', 'error')
      } else {
        ;(window as any).__UAT_HEALTH__.setLoadState('vehicles', 'success')
      }
    }
  }, [vehicles])

  return vehicles
}

export const useVehiclesByProject = (projectId: Id<'projects'> | undefined) => {
  return useQuery(
    api.vehicles.listByProject,
    projectId ? { projectId } : 'skip'
  )
}

export const useVehicle = (vehicleId: Id<'vehicles'> | undefined) => {
  return useQuery(api.vehicles.getById, vehicleId ? { id: vehicleId } : 'skip')
}

export const useCreateVehicle = () => {
  return useMutation(api.vehicles.create)
}

export const useUpdateVehicle = () => {
  return useMutation(api.vehicles.update)
}

export const useDeleteVehicle = () => {
  return useMutation(api.vehicles.remove)
}

export const useDuplicateVehicles = () => {
  return useMutation(api.vehicles.duplicate)
}

// =============================================================================
// VRP-HOOK-056 TO 060: JOB HOOKS
// =============================================================================

export const useJobs = (datasetId: Id<'datasets'> | undefined) => {
  const jobs = useQuery(
    api.jobs.listByDataset,
    datasetId ? { datasetId } : 'skip'
  )

  // Update UAT health check with jobs load state
  useEffect(() => {
    if (
      process.env.NODE_ENV === 'development' &&
      (window as any).__UAT_HEALTH__
    ) {
      if (jobs === undefined) {
        ;(window as any).__UAT_HEALTH__.setLoadState('jobs', 'loading')
      } else if (jobs === null) {
        ;(window as any).__UAT_HEALTH__.setLoadState('jobs', 'error')
      } else {
        ;(window as any).__UAT_HEALTH__.setLoadState('jobs', 'success')
      }
    }
  }, [jobs])

  return jobs
}

export const useJobsByProject = (projectId: Id<'projects'> | undefined) => {
  return useQuery(api.jobs.listByProject, projectId ? { projectId } : 'skip')
}

export const useJob = (jobId: Id<'jobs'> | undefined) => {
  return useQuery(api.jobs.getById, jobId ? { id: jobId } : 'skip')
}

export const useCreateJob = () => {
  return useMutation(api.jobs.create)
}

export const useUpdateJob = () => {
  return useMutation(api.jobs.update)
}

export const useDeleteJob = () => {
  return useMutation(api.jobs.remove)
}

// =============================================================================
// VRP-HOOK-061 TO 065: LOCATION HOOKS
// =============================================================================

export const useLocations = (datasetId: Id<'datasets'> | undefined) => {
  return useQuery(
    api.locations.listByDataset,
    datasetId ? { datasetId } : 'skip'
  )
}

export const useLocationsByProject = (
  projectId: Id<'projects'> | undefined
) => {
  return useQuery(
    api.locations.listByProject,
    projectId ? { projectId } : 'skip'
  )
}

export const useLocation = (locationId: Id<'locations'> | undefined) => {
  return useQuery(
    api.locations.getById,
    locationId ? { id: locationId } : 'skip'
  )
}

export const useCreateLocation = () => {
  return useMutation(api.locations.create)
}

export const useUpdateLocation = () => {
  return useMutation(api.locations.update)
}

export const useDeleteLocation = () => {
  return useMutation(api.locations.remove)
}

// =============================================================================
// VRP-HOOK-066 TO 075: ROUTE HOOKS & ANALYSIS
// =============================================================================

export const useRoutes = (datasetId: Id<'datasets'> | undefined) => {
  const routes = useQuery(
    api.routes.listByDataset,
    datasetId ? { datasetId } : 'skip'
  )

  // Update UAT health check with routes load state
  useEffect(() => {
    if (
      process.env.NODE_ENV === 'development' &&
      (window as any).__UAT_HEALTH__
    ) {
      if (routes === undefined) {
        ;(window as any).__UAT_HEALTH__.setLoadState('routes', 'loading')
      } else if (routes === null) {
        ;(window as any).__UAT_HEALTH__.setLoadState('routes', 'error')
      } else {
        ;(window as any).__UAT_HEALTH__.setLoadState('routes', 'success')
      }
    }
  }, [routes])

  return routes
}

export const useRoutesByProject = (projectId: Id<'projects'> | undefined) => {
  return useQuery(api.routes.listByProject, projectId ? { projectId } : 'skip')
}

export const useRoute = (routeId: Id<'routes'> | undefined) => {
  return useQuery(api.routes.getById, routeId ? { id: routeId } : 'skip')
}

export const useRouteWithSteps = (routeId: Id<'routes'> | undefined) => {
  return useQuery(api.routes.getRouteWithSteps, routeId ? { routeId } : 'skip')
}

export const useRouteSteps = (routeId: Id<'routes'> | undefined) => {
  return useQuery(api.routes.getRouteSteps, routeId ? { routeId } : 'skip')
}

export const useRoutesWithGeometry = (
  optimizationRunId: Id<'optimizationRuns'> | undefined,
  projectId: Id<'projects'> | undefined
) => {
  return useQuery(
    api.routes.enrichRoutesWithRelationships,
    optimizationRunId
      ? { optimizationRunId }
      : projectId
        ? { projectId }
        : 'skip'
  )
}

export const useRoutesByOptimization = (
  optimizationRunId: Id<'optimizationRuns'> | undefined
) => {
  return useQuery(
    api.routes.listByOptimization,
    optimizationRunId ? { optimizationRunId } : 'skip'
  )
}

export const useRouteGeometry = (routeId: Id<'routes'> | undefined) => {
  return useQuery(api.routes.getRouteGeometry, routeId ? { routeId } : 'skip')
}

export const useRoutesWithFilters = (filters: {
  projectId: Id<'projects'>
  optimizationRunId?: Id<'optimizationRuns'>
  vehicleId?: Id<'vehicles'>
  hasViolations?: boolean
  minCost?: number
  maxCost?: number
  minDistance?: number
  maxDistance?: number
  limit?: number
}) => {
  return useQuery(
    api.routes.listWithFilters,
    filters.projectId ? filters : 'skip'
  )
}

export const useRouteEfficiencyMetrics = (
  optimizationRunId: Id<'optimizationRuns'> | undefined
) => {
  return useQuery(
    api.routes.getEfficiencyMetrics,
    optimizationRunId ? { optimizationRunId } : 'skip'
  )
}

// Utility hook for route analysis
export const useRouteAnalysis = (routeId: Id<'routes'> | undefined) => {
  const route = useRoute(routeId)
  const routeSteps = useRouteSteps(routeId)
  const routeGeometry = useRouteGeometry(routeId)

  // Memoize the analysis results
  const analysis = useMemo(() => {
    if (!route) return null

    return {
      hasViolations: route.violations && route.violations.length > 0,
      violationCount: route.violations?.length || 0,
      hasGeometry: !!(route.geometry || route.geojson),
      stepCount: routeSteps?.length || 0,
      efficiency: {
        costPerKm: route.distance ? (route.cost || 0) / route.distance : 0,
        avgSpeed:
          route.duration && route.distance
            ? route.distance / (route.duration / 3600)
            : 0,
        utilizationRate:
          route.serviceTime && route.duration
            ? (route.serviceTime / route.duration) * 100
            : 0,
      },
    }
  }, [route, routeSteps, routeGeometry])

  return {
    route,
    routeSteps,
    routeGeometry,
    analysis,
    isLoading: route === undefined,
  }
}

// Hook for multiple route analysis (optimization run level)
export const useOptimizationRouteAnalysis = (
  optimizationRunId: Id<'optimizationRuns'> | undefined
) => {
  const routes = useRoutesByOptimization(optimizationRunId)
  const metrics = useRouteEfficiencyMetrics(optimizationRunId)

  // Memoize the aggregated analysis
  const analysis = useMemo(() => {
    if (!routes || routes.length === 0) return null

    const totalRoutes = routes.length
    const routesWithViolations = routes.filter(
      r => r.violations && r.violations.length > 0
    ).length
    const routesWithGeometry = routes.filter(
      r => !!(r.geometry || r.geojson)
    ).length

    const totals = routes.reduce(
      (acc, route) => ({
        cost: acc.cost + (route.cost || 0),
        distance: acc.distance + (route.distance || 0),
        duration: acc.duration + (route.duration || 0),
        serviceTime: acc.serviceTime + (route.serviceTime || 0),
        deliveryCount: acc.deliveryCount + (route.deliveryCount || 0),
      }),
      { cost: 0, distance: 0, duration: 0, serviceTime: 0, deliveryCount: 0 }
    )

    return {
      totalRoutes,
      routesWithViolations,
      routesWithGeometry,
      violationRate: (routesWithViolations / totalRoutes) * 100,
      geometryCoverage: (routesWithGeometry / totalRoutes) * 100,
      averages: {
        costPerRoute: totals.cost / totalRoutes,
        distancePerRoute: totals.distance / totalRoutes,
        durationPerRoute: totals.duration / totalRoutes,
        serviceTimePerRoute: totals.serviceTime / totalRoutes,
        deliveriesPerRoute: totals.deliveryCount / totalRoutes,
      },
      totals,
    }
  }, [routes])

  return {
    routes,
    metrics,
    analysis,
    isLoading: routes === undefined,
  }
}

// Vehicle Summary Hook - Aggregated vehicle performance metrics from routes
export const useVehicleSummary = (
  optimizationRunId: Id<'optimizationRuns'> | undefined,
  projectId: Id<'projects'> | undefined
) => {
  return useQuery(
    api.routes.getVehicleSummary,
    optimizationRunId && projectId ? { optimizationRunId, projectId } : 'skip'
  )
}

// =============================================================================
// VRP-HOOK-076 TO 080: SKILLS, PRODUCTS, CLUSTERS HOOKS
// =============================================================================

export const useSkills = (projectId: Id<'projects'> | undefined) => {
  return useQuery(
    api.projects.getProjectSkills,
    projectId ? { projectId } : 'skip'
  )
}

export const useSkillsByProject = (projectId: Id<'projects'> | undefined) => {
  return useQuery(
    api.projects.getProjectSkills,
    projectId ? { projectId } : 'skip'
  )
}

export const useSkill = (skillId: Id<'skills'> | undefined) => {
  return useQuery(api.projects.getSkillById, skillId ? { id: skillId } : 'skip')
}

export const useCreateSkill = () => {
  return useMutation(api.projects.createSkill)
}

export const useUpdateSkill = () => {
  return useMutation(api.projects.updateSkill)
}

export const useDeleteSkill = () => {
  return useMutation(api.projects.removeSkill)
}

export const useSkillsWithFilters = (filters: {
  projectId?: Id<'projects'>
  category?: string
  skillType?: string
  isActive?: boolean
  requiresCertification?: boolean
  searchTerm?: string
  limit?: number
}) => {
  return useQuery(
    api.projects.getSkillsWithFilters,
    filters.projectId ? filters : 'skip'
  )
}

export const useSkillUsage = (skillId: Id<'skills'> | undefined) => {
  return useQuery(
    api.projects.getSkillUsageStatistics,
    skillId ? { skillId } : 'skip'
  )
}

// =============================================================================
// PRODUCTS HOOKS (Part of VRP-HOOK-076 TO 080)
// =============================================================================

export const useProducts = (projectId: Id<'projects'> | undefined) => {
  return useQuery(
    api.projects.listProductsByProject,
    projectId ? { projectId } : 'skip'
  )
}

export const useProductsByProject = (projectId: Id<'projects'> | undefined) => {
  return useQuery(
    api.projects.listProductsByProject,
    projectId ? { projectId } : 'skip'
  )
}

export const useProduct = (productId: Id<'products'> | undefined) => {
  return useQuery(
    api.projects.getProductById,
    productId ? { id: productId } : 'skip'
  )
}

export const useCreateProduct = () => {
  return useMutation(api.projects.createProduct)
}

export const useUpdateProduct = () => {
  return useMutation(api.projects.updateProduct)
}

export const useDeleteProduct = () => {
  return useMutation(api.projects.removeProduct)
}

// =============================================================================
// LOCATION CLUSTERS HOOKS (Part of VRP-HOOK-076 TO 080)
// =============================================================================

export const useLocationClusters = (datasetId: Id<'datasets'> | undefined) => {
  return useQuery(
    api.locationClusters.listByDataset,
    datasetId ? { datasetId } : 'skip'
  )
}

export const useLocationClustersByProject = (
  projectId: Id<'projects'> | undefined
) => {
  return useQuery(
    api.locationClusters.listByProject,
    projectId ? { projectId } : 'skip'
  )
}

export const useLocationCluster = (
  clusterId: Id<'locationClusters'> | undefined
) => {
  return useQuery(
    api.locationClusters.getById,
    clusterId ? { id: clusterId } : 'skip'
  )
}

export const useCreateLocationCluster = () => {
  return useMutation(api.locationClusters.create)
}

export const useUpdateLocationCluster = () => {
  return useMutation(api.locationClusters.update)
}

export const useDeleteLocationCluster = () => {
  return useMutation(api.locationClusters.remove)
}

// =============================================================================
// TABLE MANAGEMENT HOOKS (VRP-HOOK-081 TO 086)
// =============================================================================

// VRP-HOOK-082: useTableManagement - Table status and lifecycle operations
// Hook for managing table status and operations
export const useTableManagement = (datasetId: Id<'datasets'> | undefined) => {
  const [tableStatuses, setTableStatuses] = useState({
    vehicles: true,
    jobs: true,
    locations: true,
  })

  const toggleTableStatus = async (
    tableType: string,
    currentStatus: boolean
  ) => {
    setTableStatuses(prev => ({ ...prev, [tableType]: !currentStatus }))
  }

  const createTable = async (tableType: string) => {
    setTableStatuses(prev => ({ ...prev, [tableType]: true }))
  }

  const deleteTable = async (tableType: string) => {
    setTableStatuses(prev => ({ ...prev, [tableType]: false }))
  }

  return {
    tableStatuses,
    toggleTableStatus,
    createTable,
    deleteTable,
  }
}

// =============================================================================
// BULK OPERATIONS HOOKS (Part of VRP-HOOK-082 TO 086)
// =============================================================================

// VRP-HOOK-083: useBulkDelete - Enterprise-grade bulk deletion with background processing
// Generic bulk delete hook for any table type
export const useBulkDelete = () => {
  return useMutation(api.projects.initiateBulkDelete)
}

// =============================================================================
// UTILITY HOOKS (Part of VRP-HOOK-082 TO 086)
// =============================================================================

// VRP-HOOK-084: useVRPDataset - Combined hook for complete dataset retrieval
// Combined hook for getting all VRP data for a dataset
export const useVRPDataset = (datasetId: Id<'datasets'> | undefined) => {
  const dataset = useDataset(datasetId)
  const vehicles = useVehicles(datasetId)
  const jobs = useJobs(datasetId)
  const locations = useLocations(datasetId)
  const routes = useRoutes(datasetId)
  const stats = useDatasetStats(datasetId)

  // Memoize the loading state calculation to prevent unnecessary re-renders
  const isLoading = useMemo(() => {
    return (
      dataset === undefined ||
      vehicles === undefined ||
      jobs === undefined ||
      locations === undefined ||
      routes === undefined
    )
  }, [dataset, vehicles, jobs, locations, routes])

  return {
    dataset,
    vehicles,
    jobs,
    locations,
    routes,
    stats,
    isLoading,
  }
}

// =============================================================================
// VRP-HOOK-087 TO 098: useTableData MEGA-HOOK (750+ lines)
// CRITICAL: Sub-50ms SLO, type-safe filtering, TBL-ARCH integration
// =============================================================================

// VRP-HOOK-087: Polymorphic 6-table retrieval (vehicles, jobs, locations, routes, skills, products)
// Enhanced table data hook with backend capabilities - Phase 1.2
export const useTableData = (
  tableType:
    | 'vehicles'
    | 'jobs'
    | 'locations'
    | 'routes'
    | 'skills'
    | 'products',
  datasetId: Id<'datasets'> | undefined,
  options?: {
    pageSize?: number
    enableEnrichment?: boolean
    projectId?: Id<'projects'> // Required for skills and products table types
    // ENHANCED FILTERING OPTIONS - ADDRESSES CRITICAL PERFORMANCE AND TYPE SAFETY ISSUES
    enableFiltering?: boolean
    debounceMs?: number
    enablePerformanceMonitoring?: boolean
    maxRecordsBeforeServerFiltering?: number
    performanceThresholdMs?: number
  }
) => {
  const {
    pageSize = 100, // Increased default for better initial load UX
    enableEnrichment = true,
    projectId,
    // ENHANCED FILTERING DEFAULTS - FIXES CRITICAL PERFORMANCE ISSUES
    enableFiltering = true,
    debounceMs = 300,
    enablePerformanceMonitoring = true,
    maxRecordsBeforeServerFiltering = 500,
    performanceThresholdMs = 50,
  } = options || {}

  // CRITICAL: Pagination is now MANDATORY - NO fallback to non-paginated queries
  const USE_PAGINATION = true

  // =============================================================================
  // ENHANCED FILTER STATE MANAGEMENT - ADDRESSES CRITICAL TYPE SAFETY VIOLATIONS
  // =============================================================================

  const [filterState, setFilterState] = useState<EnhancedFilterState>({
    filters: {},
    globalSearch: '',
    isGlobalSearchActive: false,
    visibleFilters: new Set<string>(),
    activeFilterCount: 0,
    hasActiveFilters: false,
    lastAppliedAt: Date.now(),
    error: null,
    isLoading: false,
    performanceMetrics: {
      filterDuration: 0,
      recordsProcessed: 0,
      recordsFiltered: 0,
      efficiency: 100,
      isPerformant: true,
      memoryUsage: 0,
      lastFilterTime: Date.now(),
      slowFilterWarnings: [],
      optimizationRecommendations: [],
      isWithinSLO: true,
    },
    appliedFilterCount: 0,
    resultCount: 0,
    originalDataCount: 0,
  })

  // CRITICAL FIX: Separate user-controlled filter criteria from derived metrics
  // This breaks the circular dependency: filterCriteria updates do NOT trigger filterComputationResult
  // unless the user actually changes filters (not when performance metrics update)
  const [filterCriteria, setFilterCriteria] = useState<
    Record<string, FilterFieldState>
  >({})

  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [isFiltering, setIsFiltering] = useState(false)

  // =============================================================================

  // Performance monitoring refs
  const filterHistoryRef = useRef<number[]>([])

  // Performance timing refs - prevent duplicate markStep calls on re-renders
  const timingMarkedRef = useRef<Set<string>>(new Set())

  // CRITICAL FIX (2025-10-21): Track previous metrics to prevent infinite loop
  // The infinite loop occurred when useEffect updated filterState.performanceMetrics,
  // causing filterComputationResult to recalculate on every mutation, triggering
  // useEffect again. Now we only update if metrics VALUES actually changed.
  const prevMetricsRef = useRef<any>(null)

  // EMERGENCY CIRCUIT BREAKER (Phase 0): Prevent catastrophic infinite loops
  // Stops execution if same useEffect runs 5+ times in 1 second
  const effectRunCountRef = useRef(0)
  const lastRunTimestampRef = useRef(0)

  // Helper: Mark timing step only once per hook instance
  const markStepOnce = useCallback((step: string, metadata?: any) => {
    if (!timingMarkedRef.current.has(step)) {
      try {
        perfDebug.markStep(step, metadata)
        timingMarkedRef.current.add(step)
      } catch (error) {
        // Silently skip if no active performance session
        if (import.meta.env.DEV) {
          console.debug(
            `[useVRPData] Performance session not active, skipping ${step} timing`
          )
        }
      }
    }
  }, [])

  // PHASE 1 STABILIZATION: Helper to prevent unnecessary state updates
  // Compares actual values instead of object references to prevent infinite loops
  const shouldUpdateFilterState = useCallback(
    (prev: EnhancedFilterState, updates: Partial<EnhancedFilterState>) => {
      return Object.keys(updates).some(key => {
        const prevVal = prev[key as keyof EnhancedFilterState]
        const nextVal = updates[key as keyof EnhancedFilterState]

        // Deep compare for objects/arrays
        if (
          typeof prevVal === 'object' &&
          prevVal !== null &&
          typeof nextVal === 'object' &&
          nextVal !== null
        ) {
          return JSON.stringify(prevVal) !== JSON.stringify(nextVal)
        }

        return prevVal !== nextVal
      })
    },
    []
  )

  // =============================================================================
  // DEBOUNCED SEARCH WITH PERFORMANCE MONITORING
  // =============================================================================

  const debouncedSearchUpdate = useMemo(
    () =>
      debounce((searchTerm: string) => {
        setDebouncedSearch(searchTerm)
      }, debounceMs),
    [debounceMs]
  )

  useEffect(() => {
    if (enableFiltering) {
      debouncedSearchUpdate(filterState.globalSearch)
    }
  }, [filterState.globalSearch, debouncedSearchUpdate, enableFiltering])

  // =============================================================================
  // SEARCH FIELD CONFIGURATION BY TABLE TYPE - ADDRESSES PERFORMANCE ISSUES
  // =============================================================================

  const searchFieldConfigs = useMemo(() => {
    const configs: Record<string, Array<{ field: string; weight: number }>> = {
      vehicles: [
        { field: 'description', weight: 3 },
        { field: 'reference', weight: 3 },
        { field: 'profile', weight: 2 },
        { field: '_id', weight: 1 },
      ],
      jobs: [
        { field: 'description', weight: 3 },
        { field: 'reference', weight: 3 },
        { field: '_id', weight: 1 },
        { field: 'locationId', weight: 1 },
      ],
      locations: [
        { field: 'name', weight: 4 },
        { field: 'address', weight: 3 },
        { field: 'description', weight: 2 },
        { field: 'locationType', weight: 2 },
        { field: '_id', weight: 1 },
      ],
      routes: [
        { field: 'vehicleId', weight: 3 },
        { field: '_id', weight: 2 },
        { field: 'optimizationRunId', weight: 1 },
      ],
      skills: [
        { field: 'name', weight: 4 },
        { field: 'description', weight: 3 },
        { field: 'skillCode', weight: 3 },
        { field: 'category', weight: 2 },
        { field: '_id', weight: 1 },
      ],
      products: [
        { field: 'name', weight: 4 },
        { field: 'category', weight: 3 },
        { field: 'unitType', weight: 2 },
        { field: '_id', weight: 1 },
      ],
    }
    return configs[tableType] || []
  }, [tableType])

  // Schema from backend
  const schema = useQuery(api.projects.getTableSchemaInfo, { tableType })

  // =============================================================================
  // OPTIMIZATION RUN SELECTOR STATE (Routes Table Only)
  // =============================================================================
  // Get available optimization runs for routes dropdown selector
  const optimizationRuns = useQuery(
    api.routes.listOptimizationRunsForDataset,
    tableType === 'routes' && datasetId
      ? {
          datasetId,
        }
      : 'skip'
  )

  // State to track selected optimization run (Routes only)
  const [selectedOptimizationRunId, setSelectedOptimizationRunId] =
    useState<Id<'optimizationRuns'> | null>(null)

  // Auto-select latest optimization run when list loads
  useEffect(() => {
    if (
      tableType === 'routes' &&
      optimizationRuns &&
      optimizationRuns.length > 0 &&
      !selectedOptimizationRunId
    ) {
      // Select the most recent run (first in list)
      setSelectedOptimizationRunId(optimizationRuns[0]._id)
    }
  }, [tableType, optimizationRuns, selectedOptimizationRunId])

  // Reset selection when table type or dataset changes
  useEffect(() => {
    if (tableType !== 'routes') {
      setSelectedOptimizationRunId(null)
    }
  }, [tableType, datasetId])

  // Skills are project-scoped, not dataset-scoped
  const basicSkills = useSkills(tableType === 'skills' ? projectId : undefined)
  // Products are project-scoped, not dataset-scoped
  const basicProducts = useProducts(
    tableType === 'products' ? projectId : undefined
  )

  // =============================================================================
  // MANDATORY PAGINATED DATA FETCHING - ALL TABLE TYPES
  // =============================================================================
  // CRITICAL: All queries use usePaginatedQuery - NO fallback to non-paginated

  // Performance timing: Mark query START for vehicles
  if (tableType === 'vehicles' && datasetId) {
    markStepOnce('data:query-vehicles-start', { tableType: 'vehicles' })
  }

  const paginatedVehicles = usePaginatedQuery(
    api.vehicles.listByDatasetPaginated,
    tableType === 'vehicles' && datasetId ? { datasetId } : 'skip',
    { initialNumItems: pageSize }
  )

  // Performance timing: Mark query START for jobs
  if (tableType === 'jobs' && datasetId) {
    markStepOnce('data:query-jobs-start', { tableType: 'jobs' })
  }

  const paginatedJobs = usePaginatedQuery(
    api.jobs.listByDatasetPaginated,
    tableType === 'jobs' && datasetId ? { datasetId } : 'skip',
    { initialNumItems: pageSize }
  )

  // Performance timing: Mark query START for locations
  if (tableType === 'locations' && datasetId) {
    markStepOnce('data:query-locations-start', { tableType: 'locations' })
  }

  const paginatedLocations = usePaginatedQuery(
    api.locations.listByDatasetPaginated,
    tableType === 'locations' && datasetId ? { datasetId } : 'skip',
    { initialNumItems: pageSize }
  )

  // Routes: Use enriched paginated query with optimization run filter
  const paginatedRoutes = usePaginatedQuery(
    api.routes.enrichRoutesWithRelationshipsPaginated,
    tableType === 'routes' && datasetId && selectedOptimizationRunId
      ? {
          datasetId,
          optimizationRunId: selectedOptimizationRunId,
        }
      : 'skip',
    { initialNumItems: pageSize }
  )

  // Skills: Paginated project-scoped query
  const paginatedSkills = usePaginatedQuery(
    api.skills.listByProjectPaginated,
    tableType === 'skills' && projectId ? { projectId } : 'skip',
    { initialNumItems: pageSize }
  )

  // Products: Paginated project-scoped query
  const paginatedProducts = usePaginatedQuery(
    api.products.listByProjectPaginated,
    tableType === 'products' && projectId ? { projectId } : 'skip',
    { initialNumItems: pageSize }
  )

  // CRITICAL FIX: Enrichment disabled for paginated queries
  // The backend function 'enrichJobsWithRelationshipsPaginated' does not exist
  // Backend only provides: enrichJobsWithRelationships (non-paginated)
  // For paginated mode, we use raw data only to maintain DRY principles
  // Client-side enrichment could be added later if needed

  // =============================================================================
  // TWO-PHASE DATA LOADING - REACT-ASYNC-003
  // Get raw data first (Phase 1), enrichment disabled for paginated mode (Phase 2)
  // =============================================================================

  // Phase 1: Raw data (fast, always available)
  // CRITICAL: Always use paginated results - NO fallback to non-paginated queries
  // ✅ FIX: Use deep comparison to prevent unnecessary recalculations
  const rawData = useDeepCompareMemo(() => {
    // Performance timing: Mark data processing START
    if (true) {
      markStepOnce('data:processing-start', { tableType })
    }

    // ✅ DIAGNOSTIC: Log when rawData recalculates (development only)
    if (import.meta.env.DEV) {
      console.log('[useVRPData] rawData recalculated', {
        tableType,
        timestamp: Date.now(),
        vehicleCount: paginatedVehicles.results?.length || 0,
        jobCount: paginatedJobs.results?.length || 0,
        locationCount: paginatedLocations.results?.length || 0,
      })
    }

    let result: any[]
    switch (tableType) {
      case 'vehicles':
        result = paginatedVehicles.results || []
        break
      case 'jobs':
        result = paginatedJobs.results || []
        break
      case 'locations':
        result = paginatedLocations.results || []
        break
      case 'routes':
        result = paginatedRoutes.results || []
        break
      case 'skills':
        result = paginatedSkills.results || []
        break
      case 'products':
        result = paginatedProducts.results || []
        break
      default:
        result = []
    }

    // DEBUG: Log rawData extraction for vehicles/jobs
    if (
      import.meta.env.DEV &&
      (tableType === 'vehicles' || tableType === 'jobs')
    ) {
      const paginatedSource =
        tableType === 'vehicles' ? paginatedVehicles : paginatedJobs
      console.log(
        `[Frontend - useVRPData] rawData extraction for ${tableType}:`,
        {
          paginatedSource: {
            hasResults: 'results' in paginatedSource,
            resultsType: typeof paginatedSource.results,
            resultsIsArray: Array.isArray(paginatedSource.results),
            resultsLength: Array.isArray(paginatedSource.results)
              ? paginatedSource.results.length
              : 'N/A',
          },
          extractedRawData: {
            type: typeof result,
            isArray: Array.isArray(result),
            length: result.length,
          },
        }
      )
    }

    // Performance timing: Mark data processing END
    if (import.meta.env.DEV && result.length > 0) {
      markStepOnce('data:processing-end', {
        tableType,
        recordCount: result.length,
      })
    }

    return result
  }, [
    tableType,
    paginatedVehicles.results,
    paginatedJobs.results,
    paginatedLocations.results,
    paginatedRoutes.results,
    paginatedSkills.results,
    paginatedProducts.results,
  ])

  // Performance timing: Mark query END when results arrive
  useEffect(() => {
    if (tableType === 'vehicles' && paginatedVehicles.results) {
      markStepOnce('data:query-vehicles-end', {
        tableType: 'vehicles',
        recordCount: paginatedVehicles.results.length,
      })
    }
  }, [paginatedVehicles.results])

  useEffect(() => {
    if (tableType === 'jobs' && paginatedJobs.results) {
      markStepOnce('data:query-jobs-end', {
        tableType: 'jobs',
        recordCount: paginatedJobs.results.length,
      })
    }
  }, [paginatedJobs.results])

  useEffect(() => {
    if (tableType === 'locations' && paginatedLocations.results) {
      markStepOnce('data:query-locations-end', {
        tableType: 'locations',
        recordCount: paginatedLocations.results.length,
      })
    }
  }, [paginatedLocations.results])

  // Phase 2: Enriched data (slower, optional)
  // CRITICAL FIX: Enrichment disabled for paginated queries
  // The enrichJobsWithRelationshipsPaginated function does not exist in backend
  // Enrichment can be re-enabled if backend function is created or via client-side logic
  const enrichedData = useMemo(() => {
    // Enrichment disabled - return null to use raw data
    return null
  }, [])

  // Legacy currentData for backward compatibility
  // ✅ FIX: Use deep comparison to prevent unnecessary recalculations
  const currentData = useDeepCompareMemo(() => {
    // Use enriched data if available, otherwise raw data
    if (enrichedData && enrichedData.length > 0) {
      return enrichedData
    }
    return rawData
  }, [rawData, enrichedData])

  // =============================================================================
  // OPTIMIZATION LEVEL CALCULATION - MICRO OPTIMIZATION FOR SMALL DATASETS
  // =============================================================================

  // Calculate optimization level based on current dataset size
  const optimizationLevel = useMemo(() => {
    const dataSize = currentData?.length || 0
    if (dataSize > 10000) return 'enterprise'
    if (dataSize > 1000) return 'advanced'
    if (dataSize > 500) return 'basic'
    return 'micro' // Skip filter overhead for small datasets (<500 records)
  }, [currentData?.length])

  // Compute effective settings based on optimization level
  const effectiveSettings = useMemo(() => {
    if (optimizationLevel === 'micro') {
      return {
        enableFiltering: false,
        enablePerformanceMonitoring: false,
        debounceMs: 0,
        enableEnrichment: false,
      }
    }

    return {
      enableFiltering,
      enablePerformanceMonitoring,
      debounceMs,
      enableEnrichment,
    }
  }, [
    optimizationLevel,
    enableFiltering,
    enablePerformanceMonitoring,
    debounceMs,
    enableEnrichment,
  ])

  // Loading states - REACT-ASYNC-003: Only track raw data loading
  // CRITICAL: All tables now use pagination - consistent loading logic
  const isLoading = useMemo(() => {
    const schemaLoading = schema === undefined
    let rawDataLoading = false

    switch (tableType) {
      case 'vehicles':
        rawDataLoading = paginatedVehicles.status === 'LoadingFirstPage'
        break
      case 'jobs':
        rawDataLoading = paginatedJobs.status === 'LoadingFirstPage'
        break
      case 'locations':
        rawDataLoading = paginatedLocations.status === 'LoadingFirstPage'
        break
      case 'routes':
        rawDataLoading = paginatedRoutes.status === 'LoadingFirstPage'
        break
      case 'skills':
        rawDataLoading = paginatedSkills.status === 'LoadingFirstPage'
        break
      case 'products':
        rawDataLoading = paginatedProducts.status === 'LoadingFirstPage'
        break
    }

    return schemaLoading || rawDataLoading
  }, [
    schema,
    tableType,
    paginatedVehicles.status,
    paginatedJobs.status,
    paginatedLocations.status,
    paginatedRoutes.status,
    paginatedSkills.status,
    paginatedProducts.status,
  ])

  // =============================================================================
  // QUERY-LEVEL PERFORMANCE TRACKING - PHASE 2.3
  // =============================================================================
  // Track when each individual query resolves to identify bottlenecks

  // Track vehicles query resolution
  useEffect(() => {
    if (tableType === 'vehicles') {
      const isQueryLoading = paginatedVehicles.status === 'LoadingFirstPage'
      trackQueryResolution(
        'vehicles',
        paginatedVehicles.results,
        isQueryLoading
      )
    }
  }, [tableType, paginatedVehicles.results, paginatedVehicles.status])

  // Track jobs query resolution
  useEffect(() => {
    if (tableType === 'jobs') {
      const isQueryLoading = paginatedJobs.status === 'LoadingFirstPage'
      trackQueryResolution('jobs', paginatedJobs.results, isQueryLoading)
    }
  }, [tableType, paginatedJobs.results, paginatedJobs.status])

  // Track locations query resolution
  useEffect(() => {
    if (tableType === 'locations') {
      const isQueryLoading = paginatedLocations.status === 'LoadingFirstPage'
      trackQueryResolution(
        'locations',
        paginatedLocations.results,
        isQueryLoading
      )
    }
  }, [tableType, paginatedLocations.results, paginatedLocations.status])

  // Track routes query resolution
  useEffect(() => {
    if (tableType === 'routes') {
      const isQueryLoading = paginatedRoutes.status === 'LoadingFirstPage'
      trackQueryResolution('routes', paginatedRoutes.results, isQueryLoading)
    }
  }, [tableType, paginatedRoutes.results, paginatedRoutes.status])

  // Track skills query resolution
  useEffect(() => {
    if (tableType === 'skills') {
      const isQueryLoading = paginatedSkills.status === 'LoadingFirstPage'
      trackQueryResolution('skills', paginatedSkills.results, isQueryLoading)
    }
  }, [tableType, paginatedSkills.results, paginatedSkills.status])

  // Track products query resolution
  useEffect(() => {
    if (tableType === 'products') {
      const isQueryLoading = paginatedProducts.status === 'LoadingFirstPage'
      trackQueryResolution(
        'products',
        paginatedProducts.results,
        isQueryLoading
      )
    }
  }, [tableType, paginatedProducts.results, paginatedProducts.status])

  // Pagination controls - ALL tables support pagination
  // ✅ FIX: Use deep comparison to prevent unnecessary recalculations
  const paginationState = useDeepCompareMemo(() => {
    switch (tableType) {
      case 'vehicles':
        return paginatedVehicles
      case 'jobs':
        return paginatedJobs // FIXED: Use raw paginated data (enrichment disabled)
      case 'locations':
        return paginatedLocations
      case 'routes':
        return paginatedRoutes
      case 'skills':
        return paginatedSkills
      case 'products':
        return paginatedProducts
      default:
        return null
    }
  }, [
    tableType,
    paginatedVehicles,
    paginatedJobs,
    paginatedLocations,
    paginatedRoutes,
    paginatedSkills,
    paginatedProducts,
    // enrichedJobsPaginated removed - variable no longer exists (enrichment disabled)
  ])

  // =============================================================================
  // PERFORMANCE-OPTIMIZED FILTERING ENGINE - ADDRESSES ALL CRITICAL ISSUES
  // FIX (2025-10-21): Removed setState calls from useMemo to break infinite loop
  // =============================================================================

  // REMOVED: stableFilters useMemo (caused circular dependency)
  // Now using filterCriteria directly - see Step 1 above (line 1734)
  // Previous implementation mixed source state (user filters) with derived state (performance metrics)
  // causing infinite loop: filterState update → stableFilters recalc → filterComputationResult → loop

  // CRITICAL FIX: Pure computation only - NO STATE UPDATES IN USEMEMO
  // This prevents infinite loop: setState → re-render → useMemo runs → setState → loop
  const filterComputationResult = useMemo(() => {
    // Handle null/undefined/loading cases - CRITICAL EDGE CASE FIX
    // Skip filtering entirely for micro optimization level
    if (
      !currentData ||
      currentData.length === 0 ||
      !effectiveSettings.enableFiltering
    ) {
      return {
        filtered: currentData || [],
        metrics: null,
        error: null,
      }
    }

    const startTime = performance.now()

    try {
      let filtered = [...currentData] as TableRecord[]

      // Apply global search filter with performance optimization
      if (debouncedSearch && debouncedSearch.length >= 2) {
        const searchTerm = debouncedSearch.toLowerCase().trim()

        if (searchTerm) {
          filtered = filtered.filter(record => {
            try {
              // Use weighted search across configured fields
              return searchFieldConfigs.some(({ field }) => {
                const fieldValue = (
                  record as unknown as Record<string, unknown>
                )[field]
                if (fieldValue == null) return false

                const stringValue = String(fieldValue).toLowerCase()
                return stringValue.includes(searchTerm)
              })
            } catch (error) {
              // Handle edge case where field access fails
              console.warn(
                `Search filter error for record ${record._id}:`,
                error
              )
              return false
            }
          })
        }
      }

      // Apply field-specific filters with null safety
      // CRITICAL FIX: Using filterCriteria (user-controlled state) instead of stableFilters
      Object.entries(filterCriteria).forEach(([fieldKey, filter]) => {
        if (!filter.isActive || filter.value == null) return

        try {
          filtered = filtered.filter(record => {
            const fieldValue = (record as unknown as Record<string, unknown>)[
              fieldKey
            ]

            // Handle null/undefined field values - CRITICAL EDGE CASE FIX
            if (fieldValue == null) {
              return filter.operator === 'is_empty'
            }

            switch (filter.operator) {
              case 'equals':
                return fieldValue === filter.value
              case 'not_equals':
                return fieldValue !== filter.value
              case 'contains':
                return String(fieldValue)
                  .toLowerCase()
                  .includes(String(filter.value).toLowerCase())
              case 'not_contains':
                return !String(fieldValue)
                  .toLowerCase()
                  .includes(String(filter.value).toLowerCase())
              case 'greater_than':
                return Number(fieldValue) > Number(filter.value)
              case 'less_than':
                return Number(fieldValue) < Number(filter.value)
              case 'between':
                if (Array.isArray(filter.value) && filter.value.length === 2) {
                  const [min, max] = filter.value
                  return (
                    Number(fieldValue) >= Number(min) &&
                    Number(fieldValue) <= Number(max)
                  )
                }
                return true
              case 'in':
                if (Array.isArray(filter.value)) {
                  return filter.value.includes(fieldValue as never)
                }
                return true
              case 'not_in':
                if (Array.isArray(filter.value)) {
                  return !filter.value.includes(fieldValue as never)
                }
                return true
              case 'is_empty':
                return (
                  fieldValue == null ||
                  fieldValue === '' ||
                  (Array.isArray(fieldValue) && fieldValue.length === 0)
                )
              case 'is_not_empty':
                return (
                  fieldValue != null &&
                  fieldValue !== '' &&
                  (!Array.isArray(fieldValue) || fieldValue.length > 0)
                )
              default:
                return true
            }
          })
        } catch (error) {
          console.warn(`Field filter error for ${fieldKey}:`, error)
        }
      })

      // Performance monitoring and metrics calculation (NO STATE UPDATES)
      const endTime = performance.now()
      const filterDuration = endTime - startTime

      // Update performance history for trend analysis
      filterHistoryRef.current.push(filterDuration)
      if (filterHistoryRef.current.length > 10) {
        filterHistoryRef.current.shift()
      }

      const isWithinSLO = filterDuration <= performanceThresholdMs
      const warnings: string[] = []
      const recommendations: string[] = []

      // Generate performance warnings and recommendations
      if (!isWithinSLO) {
        warnings.push(
          `Filter operation took ${filterDuration.toFixed(1)}ms, exceeding ${performanceThresholdMs}ms SLO`
        )
      }

      if (currentData.length > maxRecordsBeforeServerFiltering) {
        recommendations.push(
          `Consider server-side filtering for datasets with ${currentData.length} records`
        )
      }

      if (filterDuration > 100) {
        recommendations.push(
          'Consider implementing virtual scrolling for better performance'
        )
      }

      // Return metrics object (will be applied in useEffect)
      return {
        filtered: filtered as typeof currentData,
        metrics: {
          filterDuration,
          recordsProcessed: currentData.length,
          recordsFiltered: filtered.length,
          efficiency:
            currentData.length > 0
              ? (filtered.length / currentData.length) * 100
              : 100,
          isPerformant: isWithinSLO,
          memoryUsage: JSON.stringify(filtered).length,
          lastFilterTime: Date.now(),
          slowFilterWarnings: warnings,
          optimizationRecommendations: recommendations,
          isWithinSLO,
        },
        error: null,
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown filtering error'
      console.error('Critical filter error:', error)

      // Return error object (will be applied in useEffect)
      return {
        filtered: currentData,
        metrics: null,
        error: errorMessage,
      }
    }
  }, [
    currentData,
    debouncedSearch,
    filterCriteria, // CRITICAL FIX: User-controlled state, no circular dependency
    searchFieldConfigs,
    performanceThresholdMs,
    maxRecordsBeforeServerFiltering,
    effectiveSettings.enableFiltering,
  ])

  // CRITICAL FIX (2025-10-21): Apply filter metrics in useEffect AFTER memoization
  // EMERGENCY PATCH: Added metrics value comparison to prevent infinite loop
  // The loop occurred because: mutation → currentData updates → filterComputationResult recalculates
  // → useEffect runs → setFilterState → filterComputationResult recalculates AGAIN → infinite loop
  // Solution: Only update filterState if metrics VALUES (not references) actually changed
  useEffect(() => {
    // EMERGENCY CIRCUIT BREAKER: Prevent catastrophic infinite loops
    const now = Date.now()

    // Reset counter if 1 second elapsed since last run
    if (now - lastRunTimestampRef.current > 1000) {
      effectRunCountRef.current = 0
    }

    effectRunCountRef.current++
    lastRunTimestampRef.current = now

    // Circuit breaker: Stop if effect runs 5+ times in 1 second
    if (effectRunCountRef.current > 5) {
      console.error(
        '[CIRCUIT BREAKER] Infinite loop detected - stopping execution'
      )
      console.error('[DEBUG] filterComputationResult:', filterComputationResult)
      console.error('[DEBUG] currentData.length:', currentData.length)
      console.error('[DEBUG] Run count:', effectRunCountRef.current)
      console.trace()
      return // STOP this execution
    }

    setIsFiltering(false)

    if (filterComputationResult.error) {
      setFilterState(prev => {
        const updates = {
          ...prev,
          error: filterComputationResult.error,
          isLoading: false,
          performanceMetrics: {
            ...prev.performanceMetrics,
            isPerformant: false,
            slowFilterWarnings: [
              `Critical error: ${filterComputationResult.error}`,
            ],
          },
        }
        // PHASE 1: Only update if values actually changed
        if (shouldUpdateFilterState(prev, updates)) {
          return updates
        }
        return prev
      })
      prevMetricsRef.current = null // Reset on error
      return
    }

    if (filterComputationResult.metrics) {
      // CRITICAL: Check if metrics VALUES actually changed (not just object references)
      const metricsChanged =
        !prevMetricsRef.current ||
        prevMetricsRef.current.recordsFiltered !==
          filterComputationResult.metrics.recordsFiltered ||
        prevMetricsRef.current.filterDuration !==
          filterComputationResult.metrics.filterDuration ||
        prevMetricsRef.current.isPerformant !==
          filterComputationResult.metrics.isPerformant

      if (metricsChanged) {
        // Only update if values actually changed - breaks the infinite loop
        prevMetricsRef.current = {
          recordsFiltered: filterComputationResult.metrics.recordsFiltered,
          filterDuration: filterComputationResult.metrics.filterDuration,
          isPerformant: filterComputationResult.metrics.isPerformant,
        }

        setFilterState(prev => {
          const updates = {
            ...prev,
            performanceMetrics: filterComputationResult.metrics,
            resultCount: filterComputationResult.filtered.length,
            originalDataCount: currentData.length,
            error: null,
            isLoading: false,
          }
          // PHASE 1: Double-check with shouldUpdateFilterState for extra safety
          if (shouldUpdateFilterState(prev, updates)) {
            return updates
          }
          return prev
        })
      }
    }
  }, [filterComputationResult, currentData.length])

  // Extract filtered data for backward compatibility
  const filteredData = filterComputationResult.filtered

  // =============================================================================
  // FILTER ACTION HANDLERS WITH PROPER ERROR HANDLING
  // =============================================================================

  const updateGlobalSearch = useCallback(
    (searchTerm: string) => {
      if (!effectiveSettings.enableFiltering) {
        throw new Error(
          `Search not available for micro optimization level. ` +
            `Dataset has ${currentData?.length || 0} records (search available for >500 records). ` +
            `This indicates a configuration error - search UI should not be accessible in micro mode.`
        )
      }

      setFilterState(prev => {
        const updates = {
          ...prev,
          globalSearch: searchTerm,
          isGlobalSearchActive: searchTerm.length >= 2,
          hasActiveFilters:
            searchTerm.length >= 2 || prev.activeFilterCount > 0,
          isLoading: true,
        }
        // PHASE 1: Only update if values actually changed
        if (shouldUpdateFilterState(prev, updates)) {
          return updates
        }
        return prev
      })
    },
    [effectiveSettings.enableFiltering, currentData]
  )

  const updateFieldFilter = useCallback(
    (fieldKey: string, operator: FilterOperator, value: FilterValue) => {
      if (!enableFiltering) return

      // CRITICAL FIX: Update filter criteria (user-controlled state)
      // This state is separate from filterState to prevent circular dependency
      setFilterCriteria(prev => ({
        ...prev,
        [fieldKey]: {
          key: fieldKey,
          operator,
          value,
          isActive: value != null && value !== '',
          isDirty: true,
          error: undefined,
        },
      }))

      // Then update display state separately (DO NOT include filters property)
      setFilterState(prev => {
        const activeCount =
          Object.values(filterCriteria).filter(f => f.isActive).length + 1 // +1 for the new filter
        const updates = {
          ...prev,
          // DO NOT UPDATE filters property here - that's in filterCriteria now
          activeFilterCount: activeCount,
          appliedFilterCount: activeCount,
          hasActiveFilters: prev.isGlobalSearchActive || activeCount > 0,
          lastAppliedAt: Date.now(),
          isLoading: true,
        }
        // PHASE 1: Only update if values actually changed
        if (shouldUpdateFilterState(prev, updates)) {
          return updates
        }
        return prev
      })
    },
    [enableFiltering, filterCriteria]
  )

  const clearFilter = useCallback(
    (fieldKey: string) => {
      if (!enableFiltering) return

      // CRITICAL FIX: Remove from filter criteria (user-controlled state)
      setFilterCriteria(prev => {
        const { [fieldKey]: removed, ...remainingFilters } = prev
        return remainingFilters
      })

      // Update display state
      setFilterState(prev => {
        const activeCount = Object.values(filterCriteria).filter(
          f => f.isActive && f.key !== fieldKey
        ).length
        const updates = {
          ...prev,
          // DO NOT UPDATE filters property - that's in filterCriteria now
          activeFilterCount: activeCount,
          appliedFilterCount: activeCount,
          hasActiveFilters: prev.isGlobalSearchActive || activeCount > 0,
        }

        // PHASE 1: Only update if values actually changed
        if (shouldUpdateFilterState(prev, updates)) {
          return updates
        }
        return prev
      })
    },
    [enableFiltering, filterCriteria]
  )

  const clearAllFilters = useCallback(() => {
    if (!enableFiltering) return

    // CRITICAL FIX: Clear filter criteria (user-controlled state)
    setFilterCriteria({})

    // Then update display state
    setFilterState(prev => {
      const updates = {
        ...prev,
        // DO NOT UPDATE filters property - that's in filterCriteria now
        globalSearch: '',
        isGlobalSearchActive: false,
        visibleFilters: new Set(),
        activeFilterCount: 0,
        appliedFilterCount: 0,
        hasActiveFilters: false,
        error: null,
      }
      // PHASE 1: Only update if values actually changed
      if (shouldUpdateFilterState(prev, updates)) {
        return updates
      }
      return prev
    })
    setDebouncedSearch('')
  }, [enableFiltering])

  const toggleFilterVisibility = useCallback(
    (fieldKey: string) => {
      if (!enableFiltering) return

      setFilterState(prev => {
        const newVisibleFilters = new Set(prev.visibleFilters)
        if (newVisibleFilters.has(fieldKey)) {
          newVisibleFilters.delete(fieldKey)
        } else {
          newVisibleFilters.add(fieldKey)
        }

        const updates = {
          ...prev,
          visibleFilters: newVisibleFilters,
        }

        // PHASE 1: Only update if values actually changed
        if (shouldUpdateFilterState(prev, updates)) {
          return updates
        }
        return prev
      })
    },
    [enableFiltering]
  )

  // =============================================================================
  // UTILITY FUNCTIONS FOR FILTERING - ADDRESSES INTEGRATION ISSUES
  // =============================================================================

  const getFilterSummary = useCallback((): string => {
    if (!enableFiltering) return 'Filtering disabled'

    const parts: string[] = []

    if (filterState.isGlobalSearchActive) {
      parts.push(`Search: "${filterState.globalSearch}"`)
    }

    // CRITICAL FIX: Use filterCriteria (user-controlled state) instead of filterState.filters
    // This ensures summary reflects actual active filters, not stale display state
    const activeFilters = Object.entries(filterCriteria)
      .filter(([, filter]) => filter.isActive)
      .map(
        ([field, filter]) => `${field}: ${filter.operator} "${filter.value}"`
      )

    parts.push(...activeFilters)

    if (parts.length === 0) return 'No active filters'

    return `${parts.length} filter${parts.length === 1 ? '' : 's'}: ${parts.join(', ')}`
  }, [
    filterState.isGlobalSearchActive,
    filterState.globalSearch,
    filterCriteria,
    enableFiltering,
  ])

  const exportFilteredData = useCallback(() => {
    return enableFiltering ? filteredData : currentData || []
  }, [filteredData, currentData, enableFiltering])

  const resetPerformanceMetrics = useCallback(() => {
    if (!enableFiltering) return

    filterHistoryRef.current = []
    setFilterState(prev => {
      const updates = {
        ...prev,
        performanceMetrics: {
          filterDuration: 0,
          recordsProcessed: 0,
          recordsFiltered: 0,
          efficiency: 100,
          isPerformant: true,
          memoryUsage: 0,
          lastFilterTime: Date.now(),
          slowFilterWarnings: [],
          optimizationRecommendations: [],
          isWithinSLO: true,
        },
      }
      // PHASE 1: Only update if values actually changed
      if (shouldUpdateFilterState(prev, updates)) {
        return updates
      }
      return prev
    })
  }, [enableFiltering])

  const getPerformanceReport =
    useCallback((): EnhancedFilterPerformanceMetrics => {
      return filterState.performanceMetrics
    }, [filterState.performanceMetrics])

  // Compute hasActiveFilters based on current state
  const hasActiveFilters =
    filterState.isGlobalSearchActive || filterState.activeFilterCount > 0

  return {
    // CRITICAL FIX: Use currentData directly - eliminates state update delay
    // REACT-ASYNC-004: Direct currentData reference ensures Convex reactivity
    // Previous bug: displayData only updated when phase === 'raw' (first load only)
    // After mutations: phase === 'enriched', so displayData never updated with new data
    // Solution: Return currentData which derives from rawData via useMemo (always fresh)
    data: effectiveSettings.enableFiltering ? filteredData : currentData, // ✅ Direct reference, always fresh from Convex
    filteredData: effectiveSettings.enableFiltering
      ? filteredData
      : currentData, // ✅ Consistent with data field
    originalData: currentData, // Original data for reference
    schema,
    isLoading, // Only reflects raw data loading, not enrichment
    pagination: paginationState,

    // OPTIMIZATION RUN SELECTOR (Routes only)
    optimizationRuns: tableType === 'routes' ? optimizationRuns : undefined, // Available runs for dropdown
    selectedOptimizationRunId:
      tableType === 'routes' ? selectedOptimizationRunId : null, // Currently selected run
    setSelectedOptimizationRunId:
      tableType === 'routes' ? setSelectedOptimizationRunId : undefined, // Setter for run selection

    // PAGINATION CONTROLS - Export for "Load More" button
    canLoadMore: {
      vehicles: paginatedVehicles.status === 'CanLoadMore',
      jobs: paginatedJobs.status === 'CanLoadMore',
      locations: paginatedLocations.status === 'CanLoadMore',
      routes: paginatedRoutes.status === 'CanLoadMore',
      skills: paginatedSkills.status === 'CanLoadMore',
      products: paginatedProducts.status === 'CanLoadMore',
    },
    loadMore: {
      vehicles: paginatedVehicles.loadMore,
      jobs: paginatedJobs.loadMore,
      locations: paginatedLocations.loadMore,
      routes: paginatedRoutes.loadMore,
      skills: paginatedSkills.loadMore,
      products: paginatedProducts.loadMore,
    },

    // OPTIMIZATION METADATA
    optimizationLevel, // Expose optimization level for debugging and UI decisions
    effectiveSettings, // Expose effective settings for transparency

    // COMPREHENSIVE FILTERING CAPABILITIES - ADDRESSES ALL CRITICAL ISSUES
    filtering: {
      // Filter state and actions
      filterState, // Display state (UI metadata)
      filterCriteria, // CRITICAL: Actual filter values (user-controlled, source of truth)
      isFiltering,
      hasActiveFilters,

      // Filter actions with proper typing
      updateGlobalSearch,
      updateFieldFilter,
      clearFilter,
      clearAllFilters,
      toggleFilterVisibility,

      // Utility functions for integration
      getFilterSummary,
      exportFilteredData, // CRITICAL: Proper filtered data export
      resetPerformanceMetrics,
      getPerformanceReport,

      // Performance monitoring
      performanceMetrics: filterState.performanceMetrics,
      isPerformanceOptimal: filterState.performanceMetrics.isWithinSLO,
    },

    // Legacy format for existing table editor
    legacySchema: schema
      ? {
          columns: Object.values(schema.fields || {})
            .sort(
              (a: any, b: any) =>
                (a.displayOrder || 999) - (b.displayOrder || 999)
            )
            .map((field: any) => ({
              key: field.key,
              label: field.label,
              type: field.dataType,
              required: field.isRequired,
              group: field.group,
              readonly: field.accessibility?.includes('readonly'),
              options: field.options,
              validation: field.validation,
              placeholder: field.placeholder,
              helpText: field.helpText,
              displayOrder: field.displayOrder, // Include displayOrder for debugging
              legacy: field.legacy, // For backwards compatibility
            })),
        }
      : { columns: [] },
  }
}

// =============================================================================
// VRP-HOOK-099 TO 105: OPTIMIZATION HOOKS
// =============================================================================

export const useOptimizationRuns = (
  scenarioId: Id<'scenarios'> | undefined
) => {
  const runs = useQuery(
    api.scenarios.listOptimizationRuns,
    scenarioId ? { scenarioId } : 'skip'
  )

  // Update UAT health check with optimization runs load state
  useEffect(() => {
    if (
      process.env.NODE_ENV === 'development' &&
      (window as any).__UAT_HEALTH__
    ) {
      if (runs === undefined) {
        ;(window as any).__UAT_HEALTH__.setLoadState(
          'optimizationRuns',
          'loading'
        )
      } else if (runs === null) {
        ;(window as any).__UAT_HEALTH__.setLoadState(
          'optimizationRuns',
          'error'
        )
      } else {
        ;(window as any).__UAT_HEALTH__.setLoadState(
          'optimizationRuns',
          'success'
        )
      }
    }
  }, [runs])

  return runs
}

export const useOptimizationRun = (
  runId: Id<'optimizationRuns'> | undefined
) => {
  return useQuery(
    api.scenarios.getOptimizationRun,
    runId ? { id: runId } : 'skip'
  )
}

export const useLatestOptimizationRun = (
  scenarioId: Id<'scenarios'> | undefined
) => {
  return useQuery(
    api.scenarios.getLatestOptimizationRun,
    scenarioId ? { scenarioId } : 'skip'
  )
}

export const useRunVROOMOptimization = () => {
  return useAction(api.scenarios.runVROOMOptimization)
}

// Combined hook for optimization workflow
export const useOptimizationWorkflow = (
  scenarioId: Id<'scenarios'> | undefined
) => {
  const runs = useOptimizationRuns(scenarioId)
  const latestRun = useLatestOptimizationRun(scenarioId)
  const runOptimization = useRunVROOMOptimization()

  // Memoize status calculations to prevent unnecessary re-renders
  const status = useMemo(() => {
    const isOptimizing = latestRun?.status === 'running'
    const hasCompleted = latestRun?.status === 'completed'
    const hasFailed = latestRun?.status === 'error'

    return {
      isOptimizing,
      hasCompleted,
      hasFailed,
      canOptimize: !isOptimizing,
    }
  }, [latestRun?.status])

  return {
    runs,
    latestRun,
    runOptimization,
    ...status,
  }
}

// =============================================================================
// VRP-HOOK-106 TO 110: CURRENCY HOOKS (NO FALLBACKS)
// =============================================================================

// Currency symbol mappings
const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  CAD: 'C$',
  AUD: 'A$',
  JPY: '¥',
  ZAR: 'R',
}

export const useCurrencyFormat = (projectId: Id<'projects'> | undefined) => {
  const project = useProject(projectId)
  // Determine current state without fallbacks
  const isLoading = !project && !!projectId
  const projectExists = !!project
  const hasCurrency = project?.currency != null && project.currency !== ''
  const isValidCurrency =
    hasCurrency && CURRENCY_SYMBOLS[project.currency] != null

  // Error state determination - explicit error messages
  let error: string | null = null
  if (!projectId) {
    error = 'No project specified'
  } else if (!isLoading && !projectExists) {
    error = 'Project not found'
  } else if (projectExists && !hasCurrency) {
    error = 'Currency not configured for this project'
  } else if (projectExists && hasCurrency && !isValidCurrency) {
    error = `Unsupported currency: ${project.currency}`
  }

  // Only provide values when everything is valid - no fallbacks
  const currency = isValidCurrency ? project.currency : null
  const symbol = isValidCurrency ? CURRENCY_SYMBOLS[project.currency] : null

  const formatters = useMemo(
    () => ({
      formatCentsToDisplay: (cents: number | null | undefined): string => {
        if (!cents) return ''
        if (!symbol)
          return `${(cents / 100).toFixed(2)} (Currency not available)`
        return `${symbol}${(cents / 100).toFixed(2)}`
      },
      centsToDollars: (cents: number | null | undefined): number =>
        cents ? cents / 100 : 0,
      dollarsToCents: (dollars: number | null | undefined): number =>
        dollars ? Math.round(dollars * 100) : 0,
      parseUserInput: (input: string): number | null => {
        const cleaned = input.replace(/[^\d.-]/g, '')
        const parsed = parseFloat(cleaned)
        return !isNaN(parsed) && parsed >= 0
          ? Math.round(parsed * 100) / 100
          : null
      },
    }),
    [symbol]
  )

  return {
    currency,
    symbol,
    isLoading,
    error,
    hasValidCurrency: isValidCurrency,
    ...formatters,
  }
}

// Utility function to check if a field is a cost field
export const isCostField = (fieldKey: string): boolean => {
  return ['costFixed', 'costPerHour', 'costPerKm'].includes(fieldKey)
}

// =============================================================================
// VRP-HOOK-111 TO 120: CAPACITY SYSTEM HOOKS (Phase 1B)
// =============================================================================

// Hook for generating vehicle capacity arrays from compartments
export const useGenerateCapacityArray = () => {
  return useMutation(api.vehicles.generateCapacityArrayMutation)
}

// Hook for calculating real-time capacity utilization
export const useCalculateCapacityUtilization = () => {
  return useQuery(api.vehicles.calculateCapacityUtilization)
}

// Hook for converting job product items to VROOM arrays
export const useCalculateJobCapacityArrays = () => {
  return useMutation(api.jobs.calculateJobCapacityArrays)
}

// Hook for real-time job-vehicle validation
export const useValidateJobAgainstVehicle = () => {
  return useMutation(api.jobs.validateJobAgainstVehicle)
}

// Enhanced hook for capacity-aware job creation with real-time validation
export const useCapacityAwareJobCreation = (
  projectId: Id<'projects'> | undefined,
  selectedVehicleId?: Id<'vehicles'>
) => {
  const products = useProducts(projectId)
  const vehicles = useVehiclesByProject(projectId)
  const calculateJobCapacity = useCalculateJobCapacityArrays()
  const validateJob = useValidateJobAgainstVehicle()

  // Real-time capacity validation for delivery items
  const validateDeliveryItems = useCallback(
    async (deliveryItems: Array<{ productId: string; quantity: number }>) => {
      if (!products || !selectedVehicleId) return null

      try {
        // Calculate job capacity requirements
        const capacityResult = await calculateJobCapacity({
          deliveryItems,
          products: products.map(p => ({
            _id: p._id,
            weightPerUnitKg: p.weightPerUnitKg || 0,
            volumePerUnitM3: p.volumePerUnitM3 || 0,
          })),
        })

        // Validate against selected vehicle
        if (capacityResult.success && capacityResult.capacityArray) {
          const validationResult = await validateJob({
            vehicleId: selectedVehicleId,
            jobCapacity: capacityResult.capacityArray,
          })

          return {
            capacityArray: capacityResult.capacityArray,
            validation: validationResult,
            isValid:
              validationResult.success && !validationResult.violations?.length,
          }
        }

        return null
      } catch (error) {
        console.error('Capacity validation error:', error)
        return null
      }
    },
    [products, selectedVehicleId, calculateJobCapacity, validateJob]
  )

  return {
    products,
    vehicles,
    selectedVehicle: vehicles?.find(v => v._id === selectedVehicleId),
    validateDeliveryItems,
    isLoading: !products || !vehicles,
  }
}

// Hook for compartment-based vehicle setup with capacity generation
export const useCompartmentVehicleSetup = (
  projectId: Id<'projects'> | undefined
) => {
  const products = useProducts(projectId)
  const generateCapacity = useGenerateCapacityArray()
  const calculateUtilization = useCalculateCapacityUtilization()

  // Generate VROOM capacity array from compartments
  const generateVehicleCapacity = useCallback(
    async (
      compartments: Array<{
        id: string
        name: string
        productTypes: string[]
        capacity: number[]
      }>,
      vehicleId: Id<'vehicles'>
    ) => {
      try {
        const result = await generateCapacity({
          vehicleId,
          compartments,
          availableProducts: products || [],
        })

        return result.success ? result.capacityArray : null
      } catch (error) {
        console.error('Capacity generation error:', error)
        return null
      }
    },
    [generateCapacity, products]
  )

  // Calculate utilization for capacity visualization
  const calculateCapacityUtilization = useCallback(
    async (usedCapacity: number[], totalCapacity: number[]) => {
      try {
        const result = await calculateUtilization({
          usedCapacity,
          totalCapacity,
        })

        return result.success ? result.utilization : null
      } catch (error) {
        console.error('Utilization calculation error:', error)
        return null
      }
    },
    [calculateUtilization]
  )

  // Get available product types for compartment configuration
  const availableProductTypes = useMemo(() => {
    if (!products) return []

    const types = new Set<string>()
    products.forEach((product: any) => {
      if (product.unitType) types.add(product.unitType)
    })

    return Array.from(types).sort()
  }, [products])

  return {
    products,
    availableProductTypes,
    generateVehicleCapacity,
    calculateCapacityUtilization,
    isLoading: !products,
  }
}

// Utility hook for capacity-related calculations and formatting
export const useCapacityUtils = () => {
  // Format capacity array for display
  const formatCapacity = useCallback((capacity: number[]) => {
    return {
      weight: `${capacity[0]?.toFixed(2) || '0.00'} kg`,
      volume: `${capacity[1]?.toFixed(3) || '0.000'} m³`,
      count: `${capacity[2] || 0} items`,
    }
  }, [])

  // Calculate capacity utilization percentage
  const calculateUtilizationPercentage = useCallback(
    (used: number[], total: number[]) => {
      return total.map((totalCap, index) => {
        if (totalCap === 0) return 0
        return Math.min(100, (used[index] / totalCap) * 100)
      })
    },
    []
  )

  // Check if capacity exceeds limits
  const isOverCapacity = useCallback((used: number[], total: number[]) => {
    return used.some((usedCap, index) => usedCap > (total[index] || 0))
  }, [])

  // Get capacity status (good, warning, critical)
  const getCapacityStatus = useCallback(
    (used: number[], total: number[]) => {
      const percentages = calculateUtilizationPercentage(used, total)
      const maxPercentage = Math.max(...percentages)

      if (maxPercentage > 100) return 'critical'
      if (maxPercentage > 80) return 'warning'
      return 'good'
    },
    [calculateUtilizationPercentage]
  )

  return {
    formatCapacity,
    calculateUtilizationPercentage,
    isOverCapacity,
    getCapacityStatus,
  }
}

// Hook for delivery quantities auto-calculation with debouncing and error handling
export const useDeliveryCalculation = (
  jobId: Id<'jobs'> | undefined,
  projectId: Id<'projects'> | undefined
) => {
  const [calculating, setCalculating] = useState(false)
  const [lastError, setLastError] = useState<string | null>(null)
  const [lastCalculatedItems, setLastCalculatedItems] = useState<string>('')

  const calculateMutation = useMutation(api.jobs.calculateDeliveryQuantities)
  const products = useProducts(projectId)

  // Array compression info for performance monitoring
  const getArrayInfo = useCallback(
    (
      deliveryItems: Array<{
        productId: string
        quantity: number
        unit: string
      }>
    ) => {
      const totalProducts = products?.length || 0
      const activeProducts = deliveryItems?.length || 0
      const arraySize = totalProducts * 3 // Weight, Volume, Count dimensions

      return {
        totalProducts,
        activeProducts,
        arraySize,
        compressionRatio:
          totalProducts > 0 ? activeProducts / totalProducts : 0,
        estimatedZeros: arraySize - activeProducts * 3,
        isLargeArray: arraySize > 150, // Performance warning threshold
      }
    },
    [products]
  )

  const triggerCalculation = useCallback(
    async (
      deliveryItems: Array<{
        productId: string
        quantity: number
        unit: string
      }>
    ) => {
      // Skip if no items or no jobId
      if (!deliveryItems || deliveryItems.length === 0 || !jobId) {
        return { success: true, deliveryArray: [] }
      }

      // Debounce: skip if calculating the same items
      const itemsKey = JSON.stringify(deliveryItems)
      if (itemsKey === lastCalculatedItems && calculating) {
        return { success: true, message: 'Calculation already in progress' }
      }

      try {
        setCalculating(true)
        setLastError(null)
        setLastCalculatedItems(itemsKey)

        // Get array info for performance monitoring
        const arrayInfo = getArrayInfo(deliveryItems)
        const startTime = performance.now()

        // Log performance warnings for large arrays
        if (arrayInfo.isLargeArray) {
          console.warn('⚠️ Large sparse array calculation starting:', {
            arraySize: arrayInfo.arraySize,
            activeProducts: arrayInfo.activeProducts,
            estimatedZeros: arrayInfo.estimatedZeros,
            compressionRatio: arrayInfo.compressionRatio,
          })
        }

        const result = await calculateMutation({
          jobId,
          deliveryItems,
          updateJob: true, // Auto-update the job's delivery field
        })

        const endTime = performance.now()
        const calculationTime = endTime - startTime

        if (result.success) {
          const finalArraySize = result.deliveryArray?.length || 0

          // Enhanced performance logging
          console.log('✅ Delivery quantities calculated:', {
            deliveryArray: result.deliveryArray,
            performanceMetrics: {
              calculationTime: Math.round(calculationTime),
              arraySize: finalArraySize,
              activeProducts: arrayInfo.activeProducts,
              compressionRatio: arrayInfo.compressionRatio,
              isOptimized:
                calculationTime < 2000 && arrayInfo.compressionRatio > 0.1,
            },
          })

          // Performance warnings for slow calculations
          if (calculationTime > 3000) {
            console.warn('⚠️ Slow calculation detected:', {
              time: Math.round(calculationTime),
              arraySize: finalArraySize,
              suggestion:
                'Consider reducing product count or optimizing array structure',
            })
          }

          return result
        } else {
          setLastError(result.error || 'Calculation failed')
          console.warn('⚠️ Delivery calculation failed:', result.error)
          return result
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown calculation error'
        setLastError(errorMessage)
        console.error('❌ Delivery calculation error:', error)
        return {
          success: false,
          error: errorMessage,
          deliveryArray: [],
        }
      } finally {
        setCalculating(false)
      }
    },
    [jobId, calculateMutation, calculating, lastCalculatedItems]
  )

  // Reset error when jobId or projectId changes
  useEffect(() => {
    setLastError(null)
    setLastCalculatedItems('')
  }, [jobId, projectId])

  return {
    calculating,
    lastError,
    triggerCalculation,
    getArrayInfo,
    clearError: () => setLastError(null),
  }
}

// =============================================================================
// VRP-HOOK-121 TO 122: PLANNING HORIZON HOOKS
// =============================================================================

/**
 * Hook to get planning horizon context for projects/scenarios
 */
export const usePlanningHorizon = (projectId: string, scenarioId?: string) => {
  // Fetch project data to get the timezone
  const project = useQuery(
    api.projects.getById,
    projectId ? { id: projectId as Id<'projects'> } : 'skip'
  )

  return useMemo(() => {
    if (!project) {
      return {
        startTime: new Date().getTime(),
        timezone: 'Africa/Johannesburg', // Default to South Africa
        formatted: 'Loading...',
        isLoading: true,
        error: null,
      }
    }

    const timezone = project.timezone || 'Africa/Johannesburg'
    const startTime = new Date().getTime() // Current time as default start

    return {
      startTime,
      timezone,
      formatted: `Current planning horizon in ${timezone}`,
      isLoading: false,
      error: null,
    }
  }, [project, projectId, scenarioId])
}
